<template>
  <div class="cash-call-management-page">
    <!-- -------------------------------- Action Table --------------------------------------------------------->
    <div class="toptable">
      <b-container class="pt-0">
      <b-row>
      <b-col lg="12">
      <BaseDataTableCashCall
        v-bind="$attrs"
        v-model="modelValue"
        ref="submittedTableRef"
        :columns="CashCallActionTableColumns"
        :items="submittedItemsProvider"
        :total="submittedTotal"
        :initial-page-size="10"
        :page-size-options="[5, 10, 100, 200, 500, 1000]"
        :tbody-tr-class="tableTbodyTrClass"
        selectable
      >

      <template v-slot:cell(status_actions)="slotProps">
        <div class="d-flex justify-content-center gap-2">

          <!-- PAID -->
          <span
            class="status-dot paid"
            :class="{ disabled: slotProps.item.Status !== 'SUBMITTED' || isReadOnlyRole }"
            v-b-tooltip.hover.left
            title="Mark as Paid"
            @click="slotProps.item.Status === 'SUBMITTED' && confirmStatusChange(slotProps.item, 'PAID')"
          /> &nbsp;&nbsp;

          <!-- REJECTED -->
          <span
            class="status-dot rejected"
            :class="{ disabled: slotProps.item.Status !== 'SUBMITTED' || isReadOnlyRole }"
            v-b-tooltip.hover.right
            title="Reject Cash Call"
            @click="slotProps.item.Status === 'SUBMITTED' && confirmStatusChange(slotProps.item, 'REJECTED')"
          />
        </div>
      </template>

      <template v-slot:header(left)>
        <div>
          <h5 class="mb-0 font-weight-bold">Submitted Cash Calls</h5>
          <slot name="header(left)" />
       </div>
      </template>

      <template v-slot:cell(details)="slotProps">

          <BaseIcon icon="eye" tag="i" :font-scale="0.9" style="cursor:pointer" 
            @click="toggleSubmitted(slotProps.item.id)"
            :class="{ 'text-primary': selectedSubmittedId === slotProps.item.id }"
            title="View this Cash Call"
          />

        <slot name="cell(actions)" v-bind="slotProps" />
      </template>

      <template v-slot:cell(created_on)="{ item }">
        <span>{{ item.created_on ? item.created_on.slice(0, 10) : '' }}</span>
      </template>

      <template v-slot:cell(submitted_on)="{ item }">
        <span>{{ item.submitted_on ? item.submitted_on.slice(0, 10) : '' }}</span>
      </template>

      <!-- Same as the All Cash Calls table: email link, full address on hover. -->
      <template v-slot:cell(Created_By)="slotProps">
        <a
          v-if="slotProps.item.Created_By"
          :href="buildMailtoLink(slotProps.item.Created_By, slotProps.item)"
          class="cash-call-mailto"
          v-b-tooltip.hover="{ customClass: 'cash-call-list-tooltip' }"
          :title="slotProps.item.Created_By"
          @click.stop
        >{{ slotProps.item.Created_By }}</a>
      </template>

      <template v-slot:cell(Modified_By)="slotProps">
        {{ formatRecipientsName(slotProps.item.Modified_By) }}
      </template>

      <template v-slot:cell(Cash_Call_Request_Amount)="slotProps">
        {{ formatCurrency(slotProps.item.Cash_Call_Request_Amount) }}
      </template>

      <!-- Co-Lead: show the primary key + a "Co-Lead (n)" badge; the full key
           list appears on hover (tooltip) - same structure as the bottom
           "All Cash Calls" table. -->
      <!-- A key too long for the column ends in "…" while the Co-Lead badge
           stays visible after it. -->
      <template v-slot:cell(Bdx_Key)="slotProps">
        <span class="cell-with-more">
          <span>{{ slotProps.item.Bdx_Key }}</span>
          <span
              v-if="slotProps.item.keys && slotProps.item.keys.length > 1"
              class="co-lead-badge"
              v-b-tooltip.hover
              :title="coLeadTooltip(slotProps.item)"
          >Co-Lead ({{ slotProps.item.keys.length }})</span>
        </span>
      </template>

      <!-- TPA / Broker / Lead Underwriter with several values (co-lead, or
           several brokers on one key): first + "(+N)", all on hover, so the row
           stays one line. -->
      <template
        v-for="listKey in listColumns"
        v-slot:[`cell(${listKey})`]="slotProps"
      >
        <span
          v-if="cellList(listKey, slotProps.item).length > 1"
          :key="listKey"
          class="cell-with-more"
        >
          <span>{{ cellList(listKey, slotProps.item)[0] }}</span>
          <span
            class="more-badge"
            v-b-tooltip.hover="{ customClass: 'cash-call-list-tooltip' }"
            :title="cellList(listKey, slotProps.item).join('\n')"
          >(+{{ cellList(listKey, slotProps.item).length - 1 }})</span>
        </span>
        <span
          v-else
          :key="listKey"
        >{{ slotProps.item[listKey] }}</span>
      </template>

      </BaseDataTableCashCall>
      
    </b-col>      
      </b-row>
    </b-container>
    </div>
    <!-- -------------------------------- Details Table --------------------------------------------------------->
    <div class="bottomtable">
      <BaseDataTable
        v-bind="$attrs"
        v-model="modelValue"
        ref="dataTableRef"
        :columns="CashCallManagementTableColumns"
        :items="itemsProvider"
        :total="total"
        :initial-page-size="100"
        :page-size-options="[100, 200, 500, 1000]"
        :tbody-tr-class="tableTbodyTrClass"
        selectable
        v-loading="loading"
        class="resizable-table"
      >
      <template v-slot:header(top)>
        <slot name="header(top)" />

        <!-- Title and Download share one row above the filters (LIQ-1973);
             pagination stays in the table footer. -->
        <div class="d-flex align-items-center justify-content-between mb-3">
          <h5 class="mb-0 font-weight-bold">All Cash Calls</h5>
          <a
            @click.prevent="handleExport"
            class="text-underline ml-4 font-weight-bold"
            href
            >Download</a>
        </div>
      </template>

      <template v-slot:cell(status_actions)="slotProps">
        <div class="d-flex justify-content-center gap-2">

          <!-- PAID -->
          <span
            class="status-dot paid"
            :class="{ disabled: slotProps.item.Status !== 'SUBMITTED' }"
            v-b-tooltip.hover.left
            title="Mark as Paid"
            @click="slotProps.item.Status === 'SUBMITTED' && confirmStatusChange(slotProps.item, 'PAID')"
          /> &nbsp;&nbsp;

          <!-- REJECTED -->
          <span
            class="status-dot rejected"
            :class="{ disabled: slotProps.item.Status !== 'SUBMITTED' }"
            v-b-tooltip.hover.right
            title="Reject Cash Call"
            @click="slotProps.item.Status === 'SUBMITTED' && confirmStatusChange(slotProps.item, 'REJECTED')"
          />
        </div>
      </template>

      <!-- A key too long for the column ends in "…" while the Co-Lead badge
           stays visible after it. -->
      <template v-slot:cell(Bdx_Key)="slotProps">
        <span class="cell-with-more">
          <span>{{ slotProps.item.Bdx_Key }}</span>
          <span
              v-if="slotProps.item.keys && slotProps.item.keys.length > 1"
              class="co-lead-badge"
              v-b-tooltip.hover
              :title="coLeadTooltip(slotProps.item)"
          >Co-Lead ({{ slotProps.item.keys.length }})</span>
        </span>
      </template>

      <template v-slot:header(left)>
        <CashCallManagementTableFilters v-model="mainFilters" />
        <slot name="header(left)" />
      </template>

      <template v-slot:header(bottom)="slotProps">
        <div class="mt-3 d-flex align-items-center justify-content-between">
          <slot name="header(bottom)" />
          <!-- <BaseButton :loading="isExporting" class="ml-auto" @click="handleExport">
            <BaseIcon
              v-if="isExported"
              icon="check-circle"
              tag="i"
              :font-scale="0.85"
              class="ml-n2"
            />
            Download
          </BaseButton> -->
      </div>
      </template>

      <template v-slot:cell(actions)="slotProps">
        <a
          :disabled="isEditDisabled(slotProps.item)"
          :style="editIconStyle(slotProps.item)" 
          role="button"
          @click.prevent="handleEdit(slotProps.item.id)"
        >
          <BaseIcon icon="pencil" tag="i" :font-scale="0.9" />
        </a>

        &nbsp;&nbsp;

        <!-- Download PDF Icon -->
        <a
          :disabled="false"
          :style="{ opacity: 1, cursor: 'pointer' }"
          role="button"
          @click.prevent="downloadCashCallPdf(slotProps.item)"
        >
          <BaseIcon icon="download" tag="i" :font-scale="0.9" />
        </a>

        &nbsp;&nbsp;

        <!-- Email Icon. The tooltip sits on a wrapper: a disabled icon has
             pointer-events: none, so a tooltip on the icon itself never shows. -->
        <span v-b-tooltip.hover.left :title="emailIconTooltip(slotProps.item)">
          <a
            :disabled="isEmailDisabled(slotProps.item)"
            :style="emailIconStyle(slotProps.item)"
            role="button"
            @click.prevent="emailCashCall(slotProps.item)"
          >
            <BaseIcon icon="envelope" tag="i" :font-scale="0.9" />
          </a>
        </span>

        &nbsp;&nbsp;

        <!-- Delete Icon -->
        <a
          :disabled="isDeleteDisabled(slotProps.item)"
          :style="deleteIconStyle(slotProps.item)"
          role="button"
          @click.prevent="handleDelete(slotProps.item)"
        >
          <BaseIcon icon="trash" tag="i" :font-scale="0.9" />
        </a>


        <slot name="cell(actions)" v-bind="slotProps" />
      </template>

      <template v-slot:cell(created_on)="{ item }">
        <span>{{ item.created_on ? item.created_on.slice(0, 10) : '' }}</span>
      </template>

      <template v-slot:cell(submitted_on)="{ item }">
        <span>{{ item.submitted_on ? item.submitted_on.slice(0, 10) : '' }}</span>
      </template>

      <template v-slot:cell(paid_on)="{ item }">
        <span>{{ item.paid_on ? item.paid_on.slice(0, 10) : '' }}</span>
      </template>

      <template v-slot:cell(rejected_on)="{ item }">
        <span>{{ item.rejected_on ? item.rejected_on.slice(0, 10) : '' }}</span>
      </template>

      <template v-slot:cell(cancelled_on)="{ item }">
        <span>{{ item.cancelled_on ? item.cancelled_on.slice(0, 10) : '' }}</span>
      </template>

      <!-- First recipient + "(+N)"; the full list shows on hover so the row
           stays one line tall. -->
      <template v-slot:cell(recipient_emails)="{ item }">
        <span v-if="splitList(item.recipient_emails).length" class="cell-with-more">
          <a
            :href="buildMailtoLink(splitList(item.recipient_emails)[0], item)"
            class="cash-call-mailto"
            v-b-tooltip.hover="{ customClass: 'cash-call-list-tooltip' }"
            :title="splitList(item.recipient_emails)[0]"
            @click.stop
          >{{ splitList(item.recipient_emails)[0] }}</a>
          <span
            v-if="splitList(item.recipient_emails).length > 1"
            class="more-badge"
            v-b-tooltip.hover="{ customClass: 'cash-call-list-tooltip' }"
            :title="splitList(item.recipient_emails).join('\n')"
          >(+{{ splitList(item.recipient_emails).length - 1 }})</span>
        </span>
      </template>

      <template v-slot:cell(Status)="slotProps">
        <span
          v-b-tooltip.hover
          :title="statusTooltip(slotProps.item)"
          class="status-badge"
        >
          <b-badge
            :variant="statusVariant(slotProps.item.Status)"
            
          >
            {{ slotProps.item.Status }}
          </b-badge>
        </span>
      </template>

      <template v-slot:cell(status_comment)="slotProps">
        <span>{{ slotProps.item.status_comment != null ? slotProps.item.status_comment : 'N/A' }}</span>
      </template>

      <!-- Comments are cut to one line; the full text shows on hover. -->
      <template v-slot:cell(cash_call_comments)="slotProps">
        <span
          class="cell-hover-text"
          v-b-tooltip.hover="{ customClass: 'cash-call-list-tooltip' }"
          :title="slotProps.item.cash_call_comments"
        >{{ slotProps.item.cash_call_comments }}</span>
      </template>

      <template v-slot:cell(Created_By)="slotProps">
        <a
          v-if="slotProps.item.Created_By"
          :href="buildMailtoLink(slotProps.item.Created_By, slotProps.item)"
          class="cash-call-mailto"
          @click.stop
        >{{ slotProps.item.Created_By }}</a>
      </template>

      <!-- Same "first + (+N)" treatment as Recipients, so several approvers
           don't stack and make the row taller. -->
      <template v-slot:cell(tpa_approvers)="slotProps">
        <span
          v-if="Array.isArray(slotProps.item.tpa_approvers) && slotProps.item.tpa_approvers.length"
          class="cell-with-more"
        >
          <a
            :href="buildMailtoLink(slotProps.item.tpa_approvers[0].email, slotProps.item)"
            target="_blank"
            rel="noopener noreferrer"
            class="cash-call-mailto"
            @click.stop
          >{{ slotProps.item.tpa_approvers[0].name }}</a>
          <span
            v-if="slotProps.item.tpa_approvers.length > 1"
            class="more-badge"
            v-b-tooltip.hover="{ customClass: 'cash-call-list-tooltip' }"
            :title="approverTooltip(slotProps.item.tpa_approvers)"
          >(+{{ slotProps.item.tpa_approvers.length - 1 }})</span>
        </span>
        <span v-else>—</span>
      </template>

      <!-- TPA / Broker / Lead Underwriter with several values (co-lead, or
           several brokers on one key): first + "(+N)", all on hover, so the row
           stays one line.
           The loop variable (listKey) must differ from the hover-text loop's
           (textKey): Vue 2 stores v-for slot templates by their slot-name
           expression, so two `cell(${sameName})` loops silently overwrite
           each other and one set of cells falls back to plain text. -->
      <template
        v-for="listKey in listColumns"
        v-slot:[`cell(${listKey})`]="slotProps"
      >
        <span
          v-if="cellList(listKey, slotProps.item).length > 1"
          :key="listKey"
          class="cell-with-more"
        >
          <span>{{ cellList(listKey, slotProps.item)[0] }}</span>
          <span
            class="more-badge"
            v-b-tooltip.hover="{ customClass: 'cash-call-list-tooltip' }"
            :title="cellList(listKey, slotProps.item).join('\n')"
          >(+{{ cellList(listKey, slotProps.item).length - 1 }})</span>
        </span>
        <span
          v-else
          :key="listKey"
        >{{ slotProps.item[listKey] }}</span>
      </template>

      <!-- Text columns the one-line rule can cut off (co-lead rows hold
           comma-joined values here): full value on hover. This table keeps the
           hover on Insured Name only. -->
      <template
        v-for="textKey in bottomHoverTextColumns"
        v-slot:[`cell(${textKey})`]="slotProps"
      >
        <span
          :key="textKey"
          class="cell-hover-text"
          v-b-tooltip.hover="{ customClass: 'cash-call-list-tooltip' }"
          :title="slotProps.item[textKey]"
        >{{ slotProps.item[textKey] }}</span>
      </template>

      <template v-slot:cell(TPA_Fees_Amount)="slotProps">
        {{ formatCurrency(slotProps.item.TPA_Fees_Amount) }}
      </template>

      <template v-slot:cell(Adjusters_Fees_Amount)="slotProps">
        {{ formatCurrency(slotProps.item.Adjusters_Fees_Amount) }}
      </template>

      <template v-slot:cell(Defence_Fees_Amount)="slotProps">
        {{ formatCurrency(slotProps.item.Defence_Fees_Amount) }}
      </template>

      <template v-slot:cell(Expenses_Amount)="slotProps">
        {{ formatCurrency(slotProps.item.Expenses_Amount) }}
      </template>

      <template v-slot:cell(Attorney_Coverage_Amount)="slotProps">
        {{ formatCurrency(slotProps.item.Attorney_Coverage_Amount) }}
      </template>

      <template v-slot:cell(Total_Fee_Amount)="slotProps">
        {{ formatCurrency(slotProps.item.Total_Fee_Amount) }}
      </template>

      <template v-slot:cell(Total_Indemnity_Amount)="slotProps">
        {{ formatCurrency(slotProps.item.Total_Indemnity_Amount) }}
      </template>

      <template v-slot:cell(Cash_Call_Request_Amount)="slotProps">
        {{ formatCurrency(slotProps.item.Cash_Call_Request_Amount) }}
      </template>

      <template v-slot:cell(Paid_Amount)="slotProps">
        {{ formatCurrency(slotProps.item.Paid_Amount) }}
      </template>

      </BaseDataTable>
      <CashCallFormSubmit
        v-model="showMailModal"
        :cashCall="selectedCashCall"
        @close="showMailModal = false"
        @submit="handleMailSubmit"
        @submit-tpa-approval="(payload) => handleMailSubmit({ ...payload, isTpaApproval: true })"
      />      
    </div>
    <!-- Status Change Model-->

    <b-modal
  v-model="showStatusModal"
  :title="statusMode === 'PAID' ? 'Mark as Paid' : (isColeadReject ? 'Reject Co-Lead UMRs' : 'Update Status')"
  hide-footer
  no-close-on-backdrop
>
  <ValidationObserver ref="statusObserver" v-slot="{ validate }">
    <!-- PAID -->
    <div v-if="statusMode === 'PAID'">
      <div class="limeiq-summary">
        <div>
          <strong style="color:#333333 !important">Total Requested Amount:</strong>
          <span class="limeiq-money">
            {{ formatCurrency(selectedStatusItem && selectedStatusItem.Cash_Call_Request_Amount) }}
          </span>
        </div>

        <div class="limeiq-breakdown">
          <span>
            <label style='color:#333333 !important'>Indemnity:</label>
            {{ formatCurrency(selectedStatusItem && selectedStatusItem.Total_Indemnity_Amount) }}
          </span>
          <span>
            <label style='color:#333333 !important'>Fees:</label>
            {{ formatCurrency(selectedStatusItem && selectedStatusItem.Total_Fee_Amount) }}
          </span>
        </div>

        <!-- Installments: only shown once something has already been paid, so a
             cash call settled in one go looks exactly as it did before. -->
        <div class="limeiq-breakdown" v-if="!isColeadPaid && standardAlreadyPaid > 0">
          <span>
            <label style='color:#333333 !important'>Already Paid:</label>
            {{ formatCurrency(standardAlreadyPaid) }}
          </span>
          <span>
            <label style='color:#333333 !important'>Remaining:</label>
            {{ formatCurrency(standardRemaining) }}
          </span>
        </div>
      </div>

      <!-- Dated payment history, same as the co-lead per-UMR view. Only rendered
           once installments exist, so a first payment looks unchanged. -->
      <div v-if="!isColeadPaid && standardPayments.length" class="standard-pay__history-wrap">
        <label class="standard-pay__history-label">Payments recorded</label>
        <table class="colead-pay__history">
          <tr v-for="p in standardPayments" :key="p.id">
            <td>
              <span v-if="p.received_date" class="colead-pay__hist-recv">Rcvd {{ p.received_date }} · </span>
              <template v-if="p.paid_date">Paid {{ p.paid_date }}</template>
              <template v-else>Paid —</template>
            </td>
            <td class="colead-pay__hist-amt">{{ formatCurrency(p.amount) }}</td>
            <td class="colead-pay__hist-cmt">{{ p.comment }}</td>
          </tr>
        </table>
      </div>

      <!-- ============================================================= -->
      <!-- CO-LEAD: Multiple partial payments PER KEY (amount + date),    -->
      <!-- each key repeatable, with per-key payment history. The cash    -->
      <!-- call stays SUBMITTED (in the top table) until every key is     -->
      <!-- fully paid, then the backend flips it to PAID.                 -->
      <!-- ============================================================= -->
      <div v-if="isColeadPaid" class="colead-pay">

        <div
          v-for="key in pagedColeadPayKeys"
          :key="key.id"
          class="colead-pay__key"
          :class="{ 'colead-pay__key--done': key.fully_paid && key.funding_required !== false }"
        >
          <!-- Collapsible header: click to expand/collapse this key's payment
               form. Always shows the UMR, a Remaining summary and the status
               badge so the modal stays compact with many keys. -->
          <div class="colead-pay__key-head colead-pay__key-head--toggle" @click="toggleColeadKey(key.id)">
            <b-icon
              :icon="coleadKeyExpanded[key.id] ? 'chevron-down' : 'chevron-right'"
              class="colead-pay__key-chevron"
            />
            <span class="colead-pay__key-title">
              {{ coleadKeyHeader(key) }}
            </span>
            <span
              v-if="!key.is_rejected && key.funding_required !== false && !key.fully_paid"
              class="colead-pay__key-remaining"
            >
              Remaining: <strong>{{ formatCurrency(coleadRemaining(key)) }}</strong>
            </span>
            <!-- Unfunded keys are checked FIRST: they don't need payment, so
                 they must read "Not funded" rather than the green "Fully paid"
                 badge (which would be misleading with $0 paid). -->
            <span class="colead-pay__key-badge colead-pay__key-badge--rejected" v-if="key.is_rejected">✕ Rejected</span>
            <span class="colead-pay__key-badge colead-pay__key-badge--pending" v-else-if="key.funding_required === false">Not funded</span>
            <!-- Finalized short settlement: a partial payment the user confirmed
                 was the final one for this UMR. Distinct from a normal "Fully
                 paid" so the underpayment is clear. -->
            <span class="colead-pay__key-badge colead-pay__key-badge--partial" v-else-if="key.payment_finalized">✓ Partially paid (final)</span>
            <span class="colead-pay__key-badge" v-else-if="key.fully_paid">✓ Fully paid</span>
          </div>

          <!-- Collapsible body -->
          <div v-show="coleadKeyExpanded[key.id]" class="colead-pay__key-body">
          <div class="colead-pay__key-figures">
            <template v-if="key.is_rejected">
              <span class="colead-pay__unfunded">This key was rejected{{ key.rejection_comment ? ' — ' + key.rejection_comment : '' }}.</span>
            </template>
            <template v-else-if="key.funding_required === false">
              <span class="colead-pay__unfunded">No funding required for this key.</span>
            </template>
            <template v-else>
              <span>Required: <strong>{{ formatCurrency(key.required_amount) }}</strong></span>
              <span>Paid: <strong>{{ formatCurrency(key.paid_amount) }}</strong></span>
            </template>
          </div>

          <!-- Payment history for this key (funded, non-rejected keys only) -->
          <template v-if="key.funding_required !== false && !key.is_rejected">
            <table class="colead-pay__history" v-if="key.payments && key.payments.length">
              <tr v-for="p in key.payments" :key="p.id">
                <td>
                  <span v-if="p.received_date" class="colead-pay__hist-recv">Rcvd {{ p.received_date }} · </span>Paid {{ p.paid_date }}
                </td>
                <td class="colead-pay__hist-amt">{{ formatCurrency(p.amount) }}</td>
                <td class="colead-pay__hist-cmt">{{ p.comment }}</td>
              </tr>
            </table>
            <p v-else class="colead-pay__no-history">No payments recorded yet.</p>
          </template>

          <!-- Add a payment to this key (hidden once fully paid, finalized as a
               short settlement, or rejected). -->
          <div v-if="!key.fully_paid && !key.payment_finalized && key.funding_required !== false && !key.is_rejected" class="colead-pay__add">
            <!-- Per-key Payment Amount: Full pre-fills THIS key's remaining
                 (still editable); Partial lets you type a partial amount. -->
            <div class="colead-pay__paytype">
              <label class="colead-pay__paytype-label">Payment Amount</label>
              <!-- No default: the user must choose Full or Partial. Defaulting
                   to Partial pre-expanded the breakdown fields before any choice
                   had been made, which read as though a partial payment was
                   already selected. -->
              <select
                class="custom-select limeiq-select colead-pay__paytype-select"
                :value="(coleadPayInputs[key.id] && coleadPayInputs[key.id].paymentType) || ''"
                @change="onColeadKeyPaymentTypeChange(key, $event.target.value)"
              >
                <option value="" disabled>Select</option>
                <option value="FULL">Full</option>
                <option value="PARTIAL">Partial</option>
              </select>
            </div>
            <!-- FULL only: the amount is this key's remaining balance. On a
                 PARTIAL the amount is DERIVED from the breakdown below (indemnity
                 + fees), exactly as the standard cash call does - a separate
                 amount box there would just duplicate the same figure and could
                 contradict it. -->
            <div class="colead-pay__add-row" v-if="coleadHasPaymentType(key) && !coleadNeedsBreakdown(key)">
              <div class="colead-pay__amt-wrap">
                <span class="colead-pay__amt-prefix">$</span>
                <input
                  class="limeiq-input colead-pay__amt"
                  placeholder="Amount"
                  :value="coleadPayInputs[key.id] && coleadPayInputs[key.id].amount"
                  @input="setColeadPayField(key.id, 'amount', $event.target.value)"
                />
              </div>
            </div>

            <!-- Paid breakdown (PARTIAL only). A FULL payment covers this key's
                 share of every requested figure, so the split is implied and no
                 input is needed. On a PARTIAL it cannot be inferred - the user
                 enters it, exactly as the standard cash call's Partial flow.
                 Only fees actually requested on the cash call appear, and each
                 ceiling is scaled to THIS key's share. -->
            <div v-if="coleadNeedsBreakdown(key)" class="colead-pay__breakdown">
              <label class="colead-pay__bd-label">Paid Indemnity</label>
              <input
                class="limeiq-input colead-pay__bd-input"
                placeholder="0.00"
                :value="coleadPayInputs[key.id] && coleadPayInputs[key.id].paid_indemnity"
                @input="setColeadPayField(key.id, 'paid_indemnity', $event.target.value)"
              />

              <template v-if="coleadFeeFields(key).length">
                <label class="colead-pay__bd-label">Paid Fees Breakdown</label>
                <!-- Same height-capped scroll area as the standard cash call, so
                     a five-fee breakdown stays compact inside the per-UMR
                     accordion instead of pushing the form down the page. -->
                <div class="limeiq-fee-scroll">
                  <div class="colead-pay__bd-grid">
                    <div
                      class="colead-pay__bd-item"
                      v-for="f in coleadFeeFields(key)"
                      :key="f.key"
                    >
                      <label class="colead-pay__bd-sub-label">
                        {{ f.label }}
                      </label>
                      <input
                        class="limeiq-input colead-pay__bd-input"
                        placeholder="0.00"
                        :value="coleadPayInputs[key.id] && coleadPayInputs[key.id][f.key]"
                        @input="setColeadPayField(key.id, f.key, $event.target.value)"
                      />
                    </div>
                  </div>
                </div>
              </template>

              <!-- Same treatment as the standard cash call's payment total:
                   dark labels, green figures. -->
              <div class="colead-pay__bd-total">
                <span>
                  <strong style="color:#333333;font-family:Segoe UI Regular !important">Total Paid Indemnity:</strong>
                  {{ formatCurrency(coleadPaidIndemnity(key)) }}
                </span>
                <span class="colead-pay__bd-divider">|</span>
                <span>
                  <strong style="color:#333333;font-family:Segoe UI Regular !important">Total Paid Fees:</strong>
                  {{ formatCurrency(coleadPaidFeesTotal(key)) }}
                </span>
                <span class="colead-pay__bd-divider">|</span>
                <span>
                  <strong style="color:#333333;font-family:Segoe UI Regular !important">Payment:</strong>
                  {{ formatCurrency(coleadPaidIndemnity(key) + coleadPaidFeesTotal(key)) }}
                </span>
              </div>
            </div>
            <!-- Two payment dates (LIQ-2017):
                 - Cash Call Received Date: date the TPA was paid by the broker.
                 - Paid to Insured Date: date the TPA paid the insured; this is
                   the date that drives inclusion on the Claims BDX export. -->
            <div class="colead-pay__dates" v-if="coleadHasPaymentType(key)">
              <div class="colead-pay__date-field">
                <label class="colead-pay__date-label">Cash Call Received Date</label>
                <DatePicker
                  :value="coleadPayInputs[key.id] && coleadPayInputs[key.id].received_date"
                  @input="setColeadPayField(key.id, 'received_date', $event)"
                  :format="DATE_FORMAT"
                  value-type="format"
                  :clearable="false"
                  class="limeiq-datepicker colead-pay__date"
                />
              </div>
              <div class="colead-pay__date-field">
                <label class="colead-pay__date-label">Paid to Insured Date</label>
                <DatePicker
                  :value="coleadPayInputs[key.id] && coleadPayInputs[key.id].date"
                  @input="setColeadPayField(key.id, 'date', $event)"
                  :format="DATE_FORMAT"
                  value-type="format"
                  :clearable="false"
                  class="limeiq-datepicker colead-pay__date"
                  :disabled-date="disabledPaidDates(selectedStatusItem && selectedStatusItem.submitted_on)"
                />
              </div>
            </div>
            <textarea
              v-if="coleadHasPaymentType(key)"
              class="limeiq-textarea colead-pay__comment"
              placeholder="Comment (optional)"
              :value="coleadPayInputs[key.id] && coleadPayInputs[key.id].comment"
              @input="setColeadPayField(key.id, 'comment', $event.target.value)"
            ></textarea>
            <small
              v-if="coleadHasPaymentType(key)"
              class="comment-counter"
              :class="{ 'comment-counter--over': commentTooLong(coleadComment(key)) }"
            >{{ commentCounterText(coleadComment(key)) }}</small>
            <!-- Per-UMR Save: records a single payment against THIS UMR only,
                 without needing to complete the other UMRs. Labelled "Save
                 Payment" (bottom-right) so it's clear each UMR saves on its own. -->
            <div class="colead-pay__add-actions" v-if="coleadHasPaymentType(key)">
              <button
                type="button"
                class="colead-pay__add-btn"
                :disabled="coleadPaySavingKeyId === key.id || commentTooLong(coleadComment(key))"
                @click="addColeadKeyPayment(key)"
              >
                {{ coleadPaySavingKeyId === key.id ? 'Saving…' : 'Save Payment' }}
              </button>
            </div>
          </div>
          <div class="invalid-feedback d-block" v-if="coleadPayErrors[key.id]">
            {{ coleadPayErrors[key.id] }}
          </div>
          <!-- Rejecting a key is done from the dedicated Reject flow (the Reject
               action on the list), not from this Mark-as-Paid modal, which is
               payment-only. -->
          </div><!-- /.colead-pay__key-body -->
        </div>

      </div>

      <div class="limeiq-field" v-if="!isColeadPaid">

        <label>Payment Amount</label>
        <select v-model="paymentType" class="custom-select limeiq-select">
          <option value="FULL">Full</option>
          <option value="PARTIAL">Partial</option>
        </select>
      </div>

      <div v-if="!isColeadPaid && paymentType === 'PARTIAL'">
        <!-- Not required once the indemnity is fully covered by earlier
             installments - the remaining balance may be fees only, and demanding
             an indemnity amount would make that final payment impossible. -->
        <ValidationProvider name="Paid Indemnity" :rules="standardIndemnityRemaining > 0 ? 'required' : ''" v-slot="{ errors }">
          <div class="limeiq-field">
            <label>Paid Indemnity</label>
            <input
              :value="paidIndemnity"
              @input="handleMoneyInput('paidIndemnity', $event.target.value)"
              class="limeiq-input"
            />
            <div class="invalid-feedback d-block" v-if="errors.length">
              {{ errors[0] }}
            </div>
            <div class="invalid-feedback d-block" v-if="statusErrors.paidIndemnity">
              {{ statusErrors.paidIndemnity }}
            </div>
          </div>
        </ValidationProvider>

        <div class="limeiq-field" v-if="activeFeeFields.length">
          <label>Paid Fees Breakdown</label>

          <div class="limeiq-fee-scroll">
            <div class="limeiq-fee-grid">
              <div
                class="limeiq-fee-item"
                v-for="f in activeFeeFields"
                :key="f.key"
              >
                <label class="limeiq-sub-label">
                  {{ f.label }} ({{ formatCurrency(f.requested) }})
                </label>

                <input
                  :value="feeBreakdown[f.key]"
                  @input="handleMoneyInput(f.key, $event.target.value)"
                  class="limeiq-input"
                  placeholder="0.00"
                />

                <div class="invalid-feedback d-block" v-if="statusErrors[f.key]">
                  {{ statusErrors[f.key] }}
                </div>
              </div>
            </div>
          </div>         

          <div class="invalid-feedback d-block" v-if="statusErrors.totalFee">
            {{ statusErrors.totalFee }}
          </div>
        </div>
        <div class="limeiq-payment-total" v-if="paymentType === 'PARTIAL'">
            <span>
              <strong style="color:#333333;font-family:Segoe UI Regular !important"> Total Paid Indemnity:</strong>
              {{ formatCurrency(paidIndemnity
                ? parseFloat(String(paidIndemnity).replace(/,/g, '')) : 0 )}}
            </span>

            <span class="limeiq-divider">|</span>

            <span>
              <strong style="color:#333333 !important;font-family:Segoe UI Regular !important">Total Paid Fees:</strong>
              {{ formatCurrency(totalPaidFee) }}
            </span>
          </div>
      </div>

      <!-- Two payment dates (LIQ-2017):
           - Cash Call Received Date: date the TPA was paid by the broker.
           - Paid to Insured Date: date the TPA paid the insured; this date
             drives inclusion on the Claims BDX export. -->
      <div class="limeiq-field" v-if="!isColeadPaid">
        <label>
          Cash Call Received Date
          <b-icon icon="info-circle" v-b-tooltip.hover="'The date the TPA was paid by the broker (cash call received).'" />
        </label>
        <DatePicker
          v-model="receivedDate"
          :format="DATE_FORMAT"
          value-type="format"
          :clearable="false"
          class="limeiq-datepicker"
        />
        <div class="invalid-feedback d-block" v-if="statusErrors.receivedDate">
          {{ statusErrors.receivedDate }}
        </div>
      </div>
      <div class="limeiq-field" v-if="!isColeadPaid">
        <label>
          Paid to Insured Date
          <b-icon icon="info-circle" v-b-tooltip.hover="'The date the TPA paid the insured. Drives inclusion on the Claims BDX export.'" />
        </label>
        <DatePicker
          v-model="paidDate"
          :format="DATE_FORMAT"
          value-type="format"
          :clearable="false"
          class="limeiq-datepicker"
          :disabled-date="disabledPaidDates(selectedStatusItem && selectedStatusItem.submitted_on)"
        />
        <div class="invalid-feedback d-block" v-if="statusErrors.paidDate">
          {{ statusErrors.paidDate }}
        </div>
      </div>

      <ValidationProvider v-if="!isColeadPaid" name="Comment" rules="required" v-slot="{ errors }">
        <div class="limeiq-field">
          <label>Comment</label>
          <textarea
            v-model="statusComment"
            class="limeiq-textarea"
            placeholder="Enter comment..."
          ></textarea>
          <small
            class="comment-counter"
            :class="{ 'comment-counter--over': commentTooLong(statusComment) }"
          >{{ commentCounterText(statusComment) }}</small>
          <div class="invalid-feedback d-block" v-if="errors.length">
            {{ errors[0] }}
          </div>
        </div>
      </ValidationProvider>
    </div>

    <div v-else-if="statusMode === 'WITHDRAWN'">
      <ValidationProvider name="Comment" rules="required" v-slot="{ errors }">
        <div class="limeiq-field">
          <label>Reason for Not Approving</label>
          <textarea
            v-model="statusComment"
            class="limeiq-textarea"
            placeholder="Enter reason..."
          ></textarea>
          <small
            class="comment-counter"
            :class="{ 'comment-counter--over': commentTooLong(statusComment) }"
          >{{ commentCounterText(statusComment) }}</small>
          <div class="invalid-feedback d-block" v-if="errors.length">
            {{ errors[0] }}
          </div>
        </div>
      </ValidationProvider>
    </div>

    <!-- CO-LEAD REJECT: reject individual keys (UMR / lead underwriter). Each
         key is rejected on its own; the others stay active. When every key is
         rejected the backend flips the whole cash call to REJECTED. -->
    <div v-else-if="isColeadReject" class="colead-pay">
      <p class="colead-reject__intro">
        Reject individual UMRs below. Each is rejected on its own, the others
        stay active. The cash call is only rejected once every UMR is.
      </p>
      <div
        v-for="key in pagedColeadPayKeys"
        :key="key.id"
        class="colead-pay__key"
        :class="{ 'colead-pay__key--done': key.is_rejected }"
      >
        <!-- Collapsible header: click to expand/collapse this key's reject form,
             so a many-UMR modal stays compact (same pattern as Mark as Paid). -->
        <div class="colead-pay__key-head colead-pay__key-head--toggle" @click="toggleColeadKey(key.id)">
          <b-icon
            :icon="coleadKeyExpanded[key.id] ? 'chevron-down' : 'chevron-right'"
            class="colead-pay__key-chevron"
          />
          <span class="colead-pay__key-title">{{ coleadKeyHeader(key) }}</span>
          <span class="colead-pay__key-badge colead-pay__key-badge--rejected" v-if="key.is_rejected">✕ Rejected</span>
          <!-- Short settlement: closed as the final payment for this UMR, so it
               is done and can no longer be rejected. -->
          <span class="colead-pay__key-badge colead-pay__key-badge--partial" v-else-if="key.payment_finalized">✓ Partially paid (final)</span>
          <span class="colead-pay__key-badge" v-else-if="key.fully_paid">✓ Fully paid</span>
        </div>

        <div v-show="coleadKeyExpanded[key.id]" class="colead-pay__key-body">
        <div class="colead-pay__key-figures">
          <template v-if="key.is_rejected">
            <span class="colead-pay__unfunded">This key was rejected{{ key.rejection_comment ? ' — ' + key.rejection_comment : '' }}.</span>
          </template>
          <template v-else-if="key.payment_finalized">
            <span class="colead-pay__unfunded">This key was closed as a final partial payment and can't be rejected.</span>
          </template>
          <template v-else-if="key.fully_paid">
            <span class="colead-pay__unfunded">This key is fully paid and can't be rejected.</span>
          </template>
          <template v-else>
            <span>Required: <strong>{{ formatCurrency(key.required_amount) }}</strong></span>
            <span>Paid: <strong>{{ formatCurrency(key.paid_amount) }}</strong></span>
          </template>
        </div>

        <!-- Reject this key (with optional comment). Hidden once rejected, fully
             paid, or closed as a final partial payment. -->
        <div v-if="!key.is_rejected && !key.fully_paid && !key.payment_finalized">
          <textarea
            class="limeiq-textarea colead-pay__comment"
            placeholder="Reason for rejecting this UMR (optional)"
            :value="coleadPayInputs[key.id] && coleadPayInputs[key.id].comment"
            @input="setColeadPayField(key.id, 'comment', $event.target.value)"
          ></textarea>
          <small
            class="comment-counter"
            :class="{ 'comment-counter--over': commentTooLong(coleadComment(key)) }"
          >{{ commentCounterText(coleadComment(key)) }}</small>
          <div class="colead-pay__reject-row">
            <button
              type="button"
              class="colead-pay__reject-btn"
              :disabled="coleadRejectingKeyId === key.id || commentTooLong(coleadComment(key))"
              @click="rejectColeadKey(key)"
            >
              {{ coleadRejectingKeyId === key.id ? 'Rejecting…' : 'Reject this key' }}
            </button>
          </div>
        </div>
        <div class="invalid-feedback d-block" v-if="coleadPayErrors[key.id]">
          {{ coleadPayErrors[key.id] }}
        </div>
        </div><!-- /.colead-pay__key-body -->
      </div>

    </div>

    <!-- REJECTED / CANCELLED (non-co-lead: whole cash call) -->
    <div v-else>
      <div class="limeiq-field limeiq-radio-group">
        <label class="limeiq-radio-label">
          <input type="radio" value="REJECTED" v-model="statusMode" />
          REJECTED
        </label>

        <label class="limeiq-radio-label">
          <input type="radio" value="CANCELLED" v-model="statusMode" />
          CANCELLED
        </label>
      </div>

      <ValidationProvider name="Comment" rules="required" v-slot="{ errors }">
        <div class="limeiq-field">
          <label>Comment</label>
          <textarea
            v-model="statusComment"
            class="limeiq-textarea"
            placeholder="Enter comment..."
          ></textarea>
          <small
            class="comment-counter"
            :class="{ 'comment-counter--over': commentTooLong(statusComment) }"
          >{{ commentCounterText(statusComment) }}</small>
          <div class="invalid-feedback d-block" v-if="errors.length">
            {{ errors[0] }}
          </div>
        </div>
      </ValidationProvider>
    </div>

    <div class="d-flex justify-content-between mt-4">
      <button type="button" class="btn base-button form-btn bg-transparent px-0 font-weight-normal shadow-none btn-white btn-md" @click="showStatusModal = false">
        Cancel
      </button>
      <!-- <BaseButton type="white"
							size="md"
							class="form-btn bg-transparent px-0 font-weight-normal shadow-none" @click="showStatusModal = false">
        Cancel
      </BaseButton> -->

      <!-- Co-lead paid AND co-lead reject are recorded per-key inline (no single
           Confirm), so the bottom action is just Close there. -->
      <BaseButton
        v-if="!isColeadPaid && !isColeadReject"
        :disabled="commentTooLong(statusComment)"
        @click="submitStatusChange"
      >
        {{ statusMode === 'PAID' ? 'Confirm Paid' : 'Confirm' }}
      </BaseButton>
    </div>
  </ValidationObserver>
</b-modal>
  </div>
</template>

<script>
import Vue from 'vue'
import {
  defineComponent,
  computed,
  ref,
  nextTick,
  watch,
  onMounted,
  getCurrentInstance
} from '@vue/composition-api'
import { createVuexHelpers } from 'vue2-helpers'
import { uniqueId } from 'lodash-es'
import { format } from 'date-fns'
import { Fragment } from 'vue-fragment'

import DatePicker from 'vue2-datepicker'
import { DATE_FORMAT, CASH_CALL_SUPPORTING_DOCUMENT_OPTIONS } from '@constants'
import { useCurrentUser, useUserCompany, useToast } from '@composables'
import CashCallService from '@services/CashCallService'

import { BaseAlert, BaseDataTable, BaseDataTableCashCall, BaseIcon, BaseButton } from '@components/base'
import { FormattedDate } from '@components/misc'
import CashCallFormSubmit from '@/components/CashCall/CashCallFormSubmit.vue';
import { CashCallManagementTableColumns, CASH_CALL_COMMENT_MAX_LENGTH } from './CashCallManagementTable.constants'
import { CashCallActionTableColumns } from './CashCallActionTable.constants'
import CashCallManagementTableFilters from './filters/CashCallManagementTableFilters.vue'
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import * as XLSX from 'xlsx'
import Swal from "sweetalert2";
import { ValidationObserver, ValidationProvider } from 'vee-validate'
// Co-lead cash calls use their own multi-UMR PDF layout (share breakdown,
// per-key account details, ...), the same one the confirmation email attaches.
// The single-key generateDocDefinition can't represent them, so downloads for a
// co-lead row build with this instead.
import { buildColeadDocDefinition, generateColeadPdfBlob } from '@util/cashCallColeadPdf'

// works in both localhost and staging
pdfMake.vfs = (pdfFonts.pdfMake && pdfFonts.pdfMake.vfs) || pdfFonts.vfs;

export default defineComponent({
    name: 'CashCallManagementTable',
    components: {
        Fragment,

    BaseAlert,
    BaseDataTable,
    BaseDataTableCashCall,
    BaseIcon,
    BaseButton,

    FormattedDate,
    CashCallManagementTableFilters,
    CashCallFormSubmit,
    ValidationObserver,
    ValidationProvider,
    DatePicker
    },    
    props: {
        value: {
        type: Array,
        default: () => []
        },
        selected: {
        type: [Number, Array],
        default: () => []
        }
    },
    emits: ['input', 'edit:item'],    
    methods: {      
      async deleteCashCall(item) {
        try {
          if (!confirm(`Are you sure you want to delete Cash Call #${item.id}?`)) {
            return;
          }

          await CashCallService.deleteCashCall(item.id);

          // App-standard toast, same as every other success message (LIQ-1195).
          Vue.prototype.$toasted.show("Cash Call deleted successfully.");

          // Refresh table after delete
          this.$refs.dataTableRef.handleRefresh();

        } catch (err) {
          console.error("Delete failed:", err);

          Vue.prototype.$toasted.show("Error deleting Cash Call.", { type: 'error' });
        }
      },

      statusVariant(status) {
        switch (status) {
          case 'PAID':
            return 'success'
          case 'PARTIALLY PAID':
            return 'warning'
          case 'REJECTED':
            return 'danger'
          case 'CANCELLED':
            return 'secondary'
          case 'DRAFT':
            return 'light'
          case 'SAVED':
            return 'light'
          case 'SUBMITTED':
            return 'info'
          case 'PENDING':
            return 'warning'
          case 'WITHDRAWN':
            return 'danger'
          default:
            return 'light'
        }
      },

      statusTooltip(item) {
        if(item.Status === 'PENDING'){
          return 'Awaiting TPA Internal Approval'
        }

        if (!item.status_comment) {
          return 'N/A'
        }

        return `Comment: ${item.status_comment}`
      },

      // generatePdfBlob(row) {
      //   return new Promise((resolve) => {    

      //     const pdf = pdfMake.createPdf(this.generateDocDefinition(row));

      //     pdf.getBlob(resolve); // return blob
      //   });
      // }
    },

    setup(props, { emit }) {
        const { showToast } = useToast()
        // Root instance proxy, used for BootstrapVue's styled confirm dialog
        // ($bvModal.msgBoxConfirm) in place of the native browser confirm().
        const vm = getCurrentInstance() && getCurrentInstance().proxy
        const showMailModal = ref(false)
        const selectedCashCall = ref(null)
        const isExporting = ref(false)
        const isExported = ref(false)
        const { useGetters } = createVuexHelpers()
        const directLinkBdxKey = ref(null)
        const directLinkCashCallId = ref(null)
        const directLinkAction = ref(null)
        // Urgent / High Importance carried on a TPA approval email's Approve link.
        const directLinkUrgent = ref(false)
        const directLinkHighImportance = ref(false)

        const { bdxFilters } = useGetters(['bdxFilters'])

        const { isSuperUser, userEmail, userId } = useCurrentUser()
        const { roleName: userCompanyRoleName, roleValue: userCompanyRoleValue } =
        useUserCompany()

        const modelValue = computed({
        get: () => props.value,
        set: (newValue) => emit('input', newValue)
        })

        const isReadOnlyRole = computed(() => {
          return ['Underwriter', 'Broker'].includes(userCompanyRoleName.value)
        })

        const mainFilters = ref({
            Status: 'All',
            created_on: [],
            total_indemnity: { from: null, to: null },
            paid_on: [],
            paid_amount: { from: null, to: null }
            })

        const dataTableRef = ref(null)
        const submittedTableRef = ref(null)


        const total = ref(0)
        const submittedTotal = ref(0)
        const loading = ref(false)
        const actionTableLoading = ref(false)
        const cashCallRecordById = ref({})
        const selectedSubmittedId = ref(null)

        const showStatusModal = ref(false)
        const statusMode = ref(null)
        const selectedStatusItem = ref(null)

        const paymentType = ref('FULL')
        const paidIndemnity = ref('')
        const paidDate = ref('')            // Paid to Insured Date (drives BDX)
        const receivedDate = ref('')        // Cash Call Received Date
        const statusComment = ref('')

        const feeBreakdown = ref({})
        const statusErrors = ref({})
        const statusObserver = ref(null)

        /* ---- Co-Lead per-key partial payments (Mark as Paid modal) ---- */
        // Whether the currently-open Mark-as-Paid modal is for a co-lead cash
        // call (per-key payment UI instead of the single Full/Partial flow).
        const isColeadPaid = computed(() =>
          statusMode.value === 'PAID' &&
          !!(selectedStatusItem.value && selectedStatusItem.value.is_co_lead)
        )
        // Co-lead rejection is PER KEY (reject one UMR/lead underwriter at a
        // time), not a single whole-cash-call reject. When the Reject dot is
        // clicked on a co-lead row we show the per-key reject list instead of
        // the plain REJECTED/CANCELLED + comment form.
        const isColeadReject = computed(() =>
          statusMode.value === 'REJECTED' &&
          !!(selectedStatusItem.value && selectedStatusItem.value.is_co_lead)
        )
        // The keys of the selected cash call, with their payment figures.
        const coleadPayKeys = computed(() => {
          const item = selectedStatusItem.value
          return (item && item.keys) || []
        })

        // ---- Co-Lead UMR list (Mark as Paid / Reject) ----
        // Every UMR is listed; the rows collapse to a one-line header so even a
        // many-UMR cash call stays compact without paging. Paging was removed
        // because it hid UMRs that the reviewer needs to action in one pass.
        const pagedColeadPayKeys = computed(() => coleadPayKeys.value)
        // Per-key { amount, date } input state, keyed by key id.
        const coleadPayInputs = ref({})
        const coleadPayErrors = ref({})
        const coleadPaySavingKeyId = ref(null)
        // Which key is currently being rejected (disables its Reject button).
        const coleadRejectingKeyId = ref(null)
        // Which co-lead keys are expanded in the Mark-as-Paid accordion:
        // { [keyId]: true }. Keeps the modal compact when there are many keys.
        const coleadKeyExpanded = ref({})
        const toggleColeadKey = (keyId) => {
          coleadKeyExpanded.value = {
            ...coleadKeyExpanded.value,
            [keyId]: !coleadKeyExpanded.value[keyId]
          }
        }

        const coleadRemaining = (key) => {
          const req = Number(key.required_amount) || 0
          const paid = Number(key.paid_amount) || 0
          return Math.max(0, req - paid)
        }

        // Per-key Full / Partial. When a key's selector is set to "Full", its
        // Amount is pre-filled with THAT key's remaining balance (still
        // editable); "Partial" clears it for manual entry. The choice is stored
        // on that key's input row so each UMR is independent.
        const onColeadKeyPaymentTypeChange = (key, value) => {
          setColeadPayField(key.id, 'paymentType', value)
          const amount = value === 'FULL' ? String(coleadRemaining(key)) : ''
          setColeadPayField(key.id, 'amount', amount)
          // Switching to FULL hides the breakdown, so clear any figures typed
          // there - otherwise they would be submitted alongside the full amount.
          if (value === 'FULL') {
            setColeadPayField(key.id, 'paid_indemnity', '')
            feeFieldConfig.forEach((f) => setColeadPayField(key.id, f.key, ''))
          }
        }

        const setColeadPayField = (keyId, field, value) => {
          // paymentType starts EMPTY - the user must pick Full or Partial before
          // any payment fields are shown.
          const next = { ...(coleadPayInputs.value[keyId] || { amount: '', date: '', received_date: '', comment: '', paymentType: '' }) }
          // Amount and every breakdown field format as currency with thousands
          // separators as you type (e.g. 1000 -> 1,000), matching the normal
          // cash call's paid inputs.
          const isMoney = field === 'amount' || field === 'paid_indemnity' ||
            feeFieldConfig.some((f) => f.key === field)
          next[field] = isMoney ? formatMoneyInput(value) : value
          coleadPayInputs.value = { ...coleadPayInputs.value, [keyId]: next }
        }

        // --- Per-key paid breakdown (LIQ-2171) --------------------------------
        // A PARTIAL payment's split across indemnity and the fee types cannot be
        // inferred from the key's share, so the user enters it - exactly as the
        // standard cash call's Partial flow does. A FULL payment needs no input:
        // it pays this key's share of every requested figure by definition.
        const coleadShareFraction = (key) => {
          const share = Number(key.share_percentage)
          // A null/0 share is treated as 100% defensively so ceilings are never
          // silently zeroed (mirrors the export's own fallback).
          if (!share || isNaN(share)) return 1
          return share / 100
        }

        // Sum of one breakdown field across every payment already recorded
        // against this key - so each ceiling can be reduced to what is still
        // OUTSTANDING rather than repeating the original figure.
        const coleadPaidSoFar = (key, field) => {
          return (key.payments || []).reduce((sum, p) => {
            const value = Number(p[field] || 0)
            return sum + (isNaN(value) ? 0 : value)
          }, 0)
        }

        // Fees shown for a key are ONLY those actually requested on the cash
        // call (requested > 0) - same rule as the standard modal, so a cash call
        // with 2 fees shows 2 fields and one with 5 shows 5. Each ceiling is
        // scaled to THIS key's share (the UMR owes its share of the fee, not the
        // whole cash call's) and then reduced by whatever earlier installments
        // already covered, so repeated payments cannot exceed the share.
        const coleadFeeFields = (key) => {
          const item = selectedStatusItem.value
          if (!item) return []
          const fraction = coleadShareFraction(key)
          return feeFieldConfig
            .map((f) => {
              const share = Number(item[f.requestedField] || 0) * fraction
              return {
                ...f,
                share,
                requested: Math.max(0, share - coleadPaidSoFar(key, f.key))
              }
            })
            // Keep a fee visible while any of THIS key's share remains
            // outstanding; a fee never requested (share 0) stays hidden.
            .filter((f) => f.share > 0 && f.requested > 0)
        }

        // This key's share of the requested indemnity, less whatever earlier
        // installments already paid - the ceiling for the Paid Indemnity input.
        const coleadIndemnityCeiling = (key) => {
          const item = selectedStatusItem.value
          if (!item) return 0
          const share = Number(item.Total_Indemnity_Amount || 0) * coleadShareFraction(key)
          return Math.max(0, share - coleadPaidSoFar(key, 'paid_indemnity'))
        }

        const coleadPayNumber = (keyId, field) => {
          const raw = (coleadPayInputs.value[keyId] || {})[field]
          if (raw === '' || raw === null || raw === undefined) return 0
          const num = parseFloat(String(raw).replace(/,/g, ''))
          return isNaN(num) ? 0 : num
        }

        const coleadPaidIndemnity = (key) => coleadPayNumber(key.id, 'paid_indemnity')

        const coleadPaidFeesTotal = (key) => {
          return coleadFeeFields(key).reduce(
            (sum, f) => sum + coleadPayNumber(key.id, f.key), 0
          )
        }

        // The breakdown is only collected on a PARTIAL payment - and only once
        // the user has actually chosen it. There is no default, so nothing is
        // shown until Full or Partial is picked.
        const coleadNeedsBreakdown = (key) => {
          const input = coleadPayInputs.value[key.id] || {}
          return input.paymentType === 'PARTIAL'
        }

        // Whether a payment type has been chosen at all - the amount field, dates
        // and Save button stay hidden until then.
        const coleadHasPaymentType = (key) => {
          const input = coleadPayInputs.value[key.id] || {}
          return input.paymentType === 'FULL' || input.paymentType === 'PARTIAL'
        }

        // Per-key header for the Mark-as-Paid modal, e.g.
        // "Brit - Bowood - B113610003C17" (Underwriter - Broker - Key). Uses a
        // plain "-" separator (no em-dashes). Broker can be comma-joined or a
        // list; take the first non-empty entry.
        const coleadKeyHeader = (key) => {
          const uw = key.Lead_Underwriter || key.TPA || ''
          let broker = key.Broker
          if (Array.isArray(broker)) broker = broker.find((b) => b && String(b).trim()) || ''
          else if (broker) broker = String(broker).split(',')[0].trim()
          const keyRef = key.UMR || key.Bdx_Key || ''
          return [uw, broker, keyRef].filter((p) => p && String(p).trim()).join(' - ')
        }

        // LIQ-1850: an over-long comment is never cut silently (a pasted 750
        // characters would quietly lose 150). It stays as typed, its counter
        // turns red with "comment is too long", and Save stays disabled until it
        // fits the 600-character column.
        const commentTooLong = (value) => (value || '').length > CASH_CALL_COMMENT_MAX_LENGTH

        const commentCounterText = (value) => {
          const counter = `${(value || '').length} / ${CASH_CALL_COMMENT_MAX_LENGTH}`
          return commentTooLong(value) ? `${counter} – comment is too long` : counter
        }

        // The comment typed for one co-lead key (payment or reject).
        const coleadComment = (key) => {
          return (coleadPayInputs.value[key.id] && coleadPayInputs.value[key.id].comment) || ''
        }

        // Message to show for a failed cash call API call: the backend's own
        // validation text (e.g. "Comment must be 600 characters or fewer"), else
        // `fallback`. Every DRF response is wrapped as { status, data }, so the
        // message is one level deeper ({detail}, {non_field_errors:[...]}, a
        // field map, or a plain string). A body that isn't that JSON envelope is
        // a server crash page (raw HTML/text, with V2's debug settings in it) and
        // is never shown - LIQ-1850.
        const apiErrorMessage = (err, fallback) => {
          const body = err && err.response && err.response.data
          if (!body || typeof body !== 'object') return fallback
          const data = body.data !== undefined ? body.data : body
          if (typeof data === 'string') return data || fallback
          if (!data || typeof data !== 'object') return fallback
          if (data.detail) return data.detail
          if (Array.isArray(data.non_field_errors) && data.non_field_errors.length) {
            return data.non_field_errors[0]
          }
          // First field error, if any (e.g. { paid_date: ["..."] }).
          const firstKey = Object.keys(data)[0]
          const firstVal = firstKey ? data[firstKey] : null
          if (Array.isArray(firstVal) && firstVal.length) return firstVal[0]
          if (typeof firstVal === 'string') return firstVal
          return fallback
        }

        const addColeadKeyPayment = async (key) => {
          coleadPayErrors.value = { ...coleadPayErrors.value, [key.id]: '' }
          const input = coleadPayInputs.value[key.id] || {}
          if (!coleadHasPaymentType(key)) {
            coleadPayErrors.value = { ...coleadPayErrors.value, [key.id]: 'Select Full or Partial first.' }
            return
          }
          // On a PARTIAL the payment IS the breakdown (indemnity + fees) - there
          // is no separate amount box to reconcile against. FULL keeps its own
          // amount field, since it has no breakdown to derive from.
          const isPartialPayment = coleadNeedsBreakdown(key)
          const amount = isPartialPayment
            ? coleadPaidIndemnity(key) + coleadPaidFeesTotal(key)
            : parseFloat(String(input.amount || '').replace(/,/g, ''))
          const date = input.date                       // Paid to Insured Date (drives BDX)
          const receivedDate = input.received_date || ''  // Cash Call Received Date
          const comment = input.comment || ''
          if (!amount || amount <= 0) {
            coleadPayErrors.value = { ...coleadPayErrors.value, [key.id]: isPartialPayment
              ? 'Enter a Paid Indemnity and/or fee amount.'
              : 'Enter a valid amount.' }
            return
          }

          // ---- Paid breakdown (PARTIAL only) ----
          // The payment IS the breakdown, so there is nothing to reconcile - the
          // only rule is that no field may exceed this key's share of that
          // requested figure (the UMR owes its share, not the whole cash call's).
          let breakdown = null
          if (isPartialPayment) {
            const indemnity = coleadPaidIndemnity(key)
            const indemnityCeiling = coleadIndemnityCeiling(key)
            if (indemnity > indemnityCeiling + 0.01) {
              coleadPayErrors.value = { ...coleadPayErrors.value, [key.id]: `Paid Indemnity cannot exceed this UMR's share (${formatCurrency(indemnityCeiling)}).` }
              return
            }
            breakdown = { paid_indemnity: indemnity }
            for (const f of coleadFeeFields(key)) {
              const value = coleadPayNumber(key.id, f.key)
              if (value > f.requested + 0.01) {
                coleadPayErrors.value = { ...coleadPayErrors.value, [key.id]: `${f.label} cannot exceed this UMR's share (${formatCurrency(f.requested)}).` }
                return
              }
              breakdown[f.key] = value
            }
          }

          // Partial payment => it does NOT settle this UMR in full. Ask whether
          // this is nonetheless the FINAL payment for the UMR (e.g. a negotiated
          // short settlement). A payment that covers the remaining in full is
          // NOT partial, so no prompt is shown then - it IS the final payment by
          // definition, hence isFinal defaults to true. The answer drives whether
          // the backend finalises (closes) the key as PARTIALLY PAID.
          //
          // This MUST run BEFORE the Paid to Insured Date check below, because
          // the answer decides whether that date is mandatory.
          const remaining = coleadRemaining(key)
          const isPartial = amount < remaining
          let isFinal = true
          if (isPartial) {
            // Spell out the consequence: finalising writes off the shortfall and
            // closes the UMR, and the form is hidden afterwards - there is no way
            // back from a mis-click short of a DB fix.
            const shortfall = remaining - amount
            // Use the setup-level `vm` (captured synchronously in setup); calling
            // getCurrentInstance() here returns null because this runs inside an
            // async event handler, not during setup.
            const answer = await vm.$bvModal.msgBoxConfirm(
              `Is this the final payment for this UMR? Choosing "Yes" closes this UMR and writes off the remaining ${formatCurrency(shortfall)}.`,
              {
                title: 'Partial Payment',
                okTitle: 'Yes, final payment',
                cancelTitle: 'No, awaiting funds',
                okVariant: 'primary',
                centered: true
              }
            )
            // Cancelling the dialog (Esc / backdrop) returns null - treat as
            // "not decided" and abort the save so the user can re-confirm.
            if (answer === null) return
            isFinal = answer === true
          }

          // Paid to Insured Date is mandatory ONLY on the final payment. While
          // interim payments are still arriving the TPA may not have paid the
          // insured yet, so there is no real date to record - forcing one would
          // put a fabricated date into paid_date, which drives the Claims BDX
          // export period. On the FINAL payment it becomes required: finalising
          // closes the key permanently, so this is the last chance to capture a
          // date, and without one the UMR would never appear on the BDX export.
          if (isFinal && !date) {
            coleadPayErrors.value = { ...coleadPayErrors.value, [key.id]: 'Select the Paid to Insured Date - it is required for the final payment.' }
            return
          }
          if (date && receivedDate && receivedDate > date) {
            coleadPayErrors.value = { ...coleadPayErrors.value, [key.id]: 'Cash Call Received Date cannot be after Paid to Insured Date.' }
            return
          }

          coleadPaySavingKeyId.value = key.id
          try {
            const { data } = await CashCallService.addKeyPayment(key.id, {
              amount,
              // Null (not '') when the user skipped it on a non-final payment -
              // paid_date is nullable on CashCallKeyPayment and an empty string
              // would fail date parsing.
              paid_date: date || null,
              received_date: receivedDate || null,
              comment,
              // True only when the user confirmed a PARTIAL payment is the final
              // one for this UMR (short settlement) - the backend then closes the
              // key as PARTIALLY PAID rather than leaving it open for more.
              is_final: isFinal,
              // Present on PARTIAL payments only; a FULL payment's split is
              // implied by this key's share.
              ...(breakdown || {})
            })
            const payload = (data && data.data) || data || {}
            // Optimistically update the in-modal figures + history for this key.
            // Include the breakdown so the per-fee ceilings drop to what is
            // still outstanding straight away, without waiting for a reload.
            const newPayment = payload.payment || {
              amount, paid_date: date, received_date: receivedDate || null, comment,
              ...(breakdown || {})
            }
            const keys = (selectedStatusItem.value.keys || []).map((k) => {
              if (k.id !== key.id) return k
              const payments = [...(k.payments || []), newPayment]
              return {
                ...k,
                payments,
                paid_amount: payload.key_paid_amount != null ? payload.key_paid_amount : (Number(k.paid_amount) || 0) + amount,
                fully_paid: payload.key_fully_paid != null ? payload.key_fully_paid : k.fully_paid,
                // Reflect a finalized short settlement immediately so the form
                // collapses and the "Partially paid (final)" badge shows without
                // waiting for a reload.
                payment_finalized: payload.key_payment_finalized != null ? payload.key_payment_finalized : (k.payment_finalized || isFinal)
              }
            })
            selectedStatusItem.value = { ...selectedStatusItem.value, keys }
            // Clear this key's input.
            setColeadPayField(key.id, 'amount', '')
            setColeadPayField(key.id, 'date', '')
            setColeadPayField(key.id, 'received_date', '')
            setColeadPayField(key.id, 'comment', '')
            // Back to no selection, so the next payment starts from a clean
            // choice rather than a pre-expanded Partial form.
            setColeadPayField(key.id, 'paymentType', '')
            // Clear the breakdown too - the ceilings have just dropped, so
            // leaving the previous figures in place would immediately read as
            // exceeding what is still outstanding.
            setColeadPayField(key.id, 'paid_indemnity', '')
            feeFieldConfig.forEach((f) => setColeadPayField(key.id, f.key, ''))
            showToast('Payment recorded')

            // Refresh the underlying list too (not just the in-modal copy).
            // The modal reopens from the list row (confirmStatusChange sets
            // selectedStatusItem = the list item), so without this a PARTIAL
            // payment showed the old paid amount until a full page reload.
            handleRefresh()

            // If the whole cash call is now fully paid, close the modal and show
            // the SAME success popup the normal cash call uses (titled for
            // co-lead), then refresh the list (it flips to PAID and drops off
            // the top table).
            if (payload.cash_call_fully_paid) {
              Vue.prototype.$emitter.emit('cashcall:email-sent', { id: selectedStatusItem.value.id })
              showStatusModal.value = false
              Swal.fire({
                icon: 'success',
                title: 'Updated',
                text: 'Cash Call - Co-Lead marked as PAID',
                buttonsStyling: false,
                confirmButtonText: 'OK',
                customClass: {
                  confirmButton: 'swal2-confirm limeiq-btn'
                }
              })
              handleRefresh()
            }
          } catch (err) {
            // Backend validation text (e.g. "Paid Date cannot be earlier than
            // Submitted Date.") rather than a generic failure.
            const detail = apiErrorMessage(err, 'Failed to record payment.')
            coleadPayErrors.value = { ...coleadPayErrors.value, [key.id]: detail }
          } finally {
            coleadPaySavingKeyId.value = null
          }
        }

        // Reject ONE co-lead key. Confirms, calls the per-key reject endpoint,
        // then updates the in-modal key + refreshes the list. Only this key is
        // rejected; the others stay active.
        const rejectColeadKey = async (key) => {
          if (coleadRejectingKeyId.value) return
          const label = key.umr || key.UMR || key.bdx_key || 'this key'
          // Styled in-app confirmation (BootstrapVue) instead of the native
          // browser confirm() popup, to match the rest of the app.
          const confirmed = await vm.$bvModal.msgBoxConfirm(
            `Reject ${label}? The other UMRs on this cash call are not affected.`,
            {
              title: 'Reject UMR',
              okTitle: 'Reject',
              okVariant: 'danger',
              cancelTitle: 'Cancel',
              centered: true
            }
          )
          if (!confirmed) return
          coleadRejectingKeyId.value = key.id
          coleadPayErrors.value = { ...coleadPayErrors.value, [key.id]: '' }
          try {
            const comment = (coleadPayInputs.value[key.id] && coleadPayInputs.value[key.id].comment) || ''
            const { data } = await CashCallService.rejectKey(key.id, comment ? { comment } : {})
            const payload = (data && data.data) || data || {}
            // Mark the in-modal key rejected so the UI updates immediately.
            const keys = (selectedStatusItem.value.keys || []).map((k) =>
              k.id === key.id ? { ...k, is_rejected: true } : k
            )
            selectedStatusItem.value = { ...selectedStatusItem.value, keys }
            showToast('Key rejected')
            // If rejecting completed the whole cash call, close + refresh.
            if (payload.cash_call_fully_paid) {
              showStatusModal.value = false
            }
            handleRefresh()
          } catch (err) {
            const detail = apiErrorMessage(err, 'Failed to reject key.')
            coleadPayErrors.value = { ...coleadPayErrors.value, [key.id]: detail }
          } finally {
            coleadRejectingKeyId.value = null
          }
        }

        // paidField is the running total already covered by earlier
        // installments - used to cap each input at what is still outstanding.
        const feeFieldConfig = [
          {
            key: 'tpa_fees_amount',
            label: 'TPA Fees',
            requestedField: 'TPA_Fees_Amount',
            paidField: 'TPA_Fees_Amount_Paid'
          },
          {
            key: 'adjuster_fee_amount',
            label: 'Adjusters Fees',
            requestedField: 'Adjusters_Fees_Amount',
            paidField: 'Adjusters_Fees_Amount_Paid'
          },
          {
            key: 'defence_fees_amount',
            label: 'Defence Fees',
            requestedField: 'Defence_Fees_Amount',
            paidField: 'Defence_Fees_Amount_Paid'
          },
          {
            key: 'expenses_amount',
            label: 'Expenses',
            requestedField: 'Expenses_Amount',
            paidField: 'Expenses_Amount_Paid'
          },
          {
            key: 'attorney_coverage_amount',
            label: 'Attorney Coverage',
            requestedField: 'Attorney_Coverage_Amount',
            paidField: 'Attorney_Coverage_Amount_Paid'
          }
        ]

        // Installments on a STANDARD cash call: what previous payments have
        // already covered, and what is still outstanding. Both are 0 on a cash
        // call being paid for the first time, so the summary is unchanged there.
        const standardAlreadyPaid = computed(() => {
          const item = selectedStatusItem.value
          if (!item) return 0
          return Number(item.Paid_Amount || 0)
        })

        const standardRemaining = computed(() => {
          const item = selectedStatusItem.value
          if (!item) return 0
          const requested = Number(item.Total_Indemnity_Amount || 0) +
            Number(item.Total_Fee_Amount || 0)
          return Math.max(0, requested - standardAlreadyPaid.value)
        })

        // Indemnity still outstanding after earlier installments. Drives whether
        // Paid Indemnity is a required field - once it reaches 0 the balance is
        // fees only.
        const standardIndemnityRemaining = computed(() => {
          const item = selectedStatusItem.value
          if (!item) return 0
          return Math.max(0,
            Number(item.Total_Indemnity_Amount || 0) - Number(item.Paid_Indemnity || 0)
          )
        })

        // Installment history for a standard cash call. It has exactly one key,
        // so its payment rows ARE the cash call's payment history.
        const standardPayments = computed(() => {
          const item = selectedStatusItem.value
          if (!item) return []
          const key = (item.keys || [])[0]
          return (key && key.payments) || []
        })

        const activeFeeFields = computed(() => {
          if (!selectedStatusItem.value) return []

          return feeFieldConfig
            .map(f => ({
              ...f,
              // Ceiling is what is still OUTSTANDING on this fee: earlier
              // installments have already covered part of it. On a first payment
              // paid_amount is 0, so this is the full requested figure.
              requested: Math.max(0,
                Number(selectedStatusItem.value[f.requestedField] || 0) -
                Number(selectedStatusItem.value[f.paidField] || 0)
              )
            }))
            .filter(f => f.requested > 0)
        })

        

        const totalPaidFee = computed(() => {
          return activeFeeFields.value.reduce((sum, f) => {
            const raw = feeBreakdown.value[f.key] == 'undefined' || feeBreakdown.value[f.key] == undefined ? '' : feeBreakdown.value[f.key]
            const num = raw === '' || raw === null || raw === undefined
              ? 0
              : parseFloat(String(raw).replace(/,/g, ''))
            return sum + (isNaN(num) ? 0 : num)
          }, 0)
        })

        const generatePdfBlob = (row, person) => {
          return new Promise((resolve) => {
            const pdf = pdfMake.createPdf(generateDocDefinition(row, person));
            pdf.getBlob(resolve); // return blob
          });   
        }        

        const handleMailSubmit = async ({ person, cashCallId, approvers = "", isTpaApproval = false, highImportance = false, urgent = false, recipientsByKey = null }) => {
          try {
              console.log('Inside handleMailSubmit')
              const cashCall = selectedCashCall.value;

              if (!cashCall || cashCall instanceof FormData) {
                console.error("Invalid cashCall object:", cashCall);
                showToast("Error sending email", "error");
                showMailModal.value = false;
                return;
              }

              // Regular email flow — generate PDF and send.
              // `selectedCashCall` can come straight from the create response,
              // which does NOT carry supporting_documents (files are uploaded in
              // a separate call afterwards). Without them every row in the PDF's
              // "Documents / Supporting Information" table rendered
              // "Not Attached", unlike the UI download which uses the list row.
              // Prefer the freshly-listed record's documents when we have them.
              // For a cash call that IS already in the list, its documents may
              // have been uploaded since the last fetch - refresh so we pick them
              // up. (A brand-new cash call isn't in the list at all; its docs
              // arrive on the success event instead, see selectedCashCall.)
              if (!(cashCall.supporting_documents && cashCall.supporting_documents.length)) {
                const cached = cashCallRecordById.value[cashCall.id]
                if (cached && !(cached.supporting_documents && cached.supporting_documents.length)) {
                  try { await handleRefresh() } catch (e) { /* non-fatal */ }
                }
              }
              const listedRecord = cashCallRecordById.value[cashCall.id] || null
              const cashCallForPdf = {
                ...cashCall,
                supporting_documents:
                  (cashCall.supporting_documents && cashCall.supporting_documents.length)
                    ? cashCall.supporting_documents
                    : ((listedRecord && listedRecord.supporting_documents) || [])
              }

              const normalizeEmails = (value) => {
                if (!value) return [];
                if (Array.isArray(value)) return String(value).split(",");
                return value;
              };

              const val = normalizeEmails(person);
              const emails = Array.isArray(val) ? val.join(",") : val;

              // Co-lead: send through the co-lead path with the co-lead PDF (every
              // UMR with its share and amount - same as the Download button), so
              // the email, its attachment and the download package all show the
              // shares. TPA approval stays on the single-key path, which is the
              // one that handles approvers.
              const coleadRow = {
                ...cashCallForPdf,
                keys: (cashCall.keys && cashCall.keys.length) ? cashCall.keys : ((listedRecord && listedRecord.keys) || [])
              }
              const sendAsColead = !isTpaApproval && isColeadRow(coleadRow)

              const pdfBlob = sendAsColead
                ? await generateColeadPdfBlob(coleadPdfArgs(coleadRow, String(emails || '').split(',').map((e) => e.trim()).filter(Boolean)))
                : await generatePdfBlob(cashCallForPdf, person);

              const formData = new FormData();

              const approversList = Array.isArray(approvers)
              ? approvers
              : (approvers ? String(approvers).split(",") : [])

              formData.append("recipient_email", emails);
              formData.append("cashcall_id", cashCallId);
              formData.append("attachment", pdfBlob, `CashCall-${cashCall.cash_call_reference}.pdf`);
              formData.append("tpa_approvers", approversList.join(","));
              formData.append("is_tpa_approval", isTpaApproval);
              // Urgency options (co-lead): high importance flag + URGENT subject.
              formData.append("high_importance", highImportance ? "true" : "false");
              formData.append("urgent", urgent ? "true" : "false");
              if (sendAsColead) {
                // The recipient modal's per-UMR picks when it gave them; otherwise
                // (resend to same recipients) everyone on every UMR, the same
                // fallback the co-lead send in CashCallDetails uses.
                const byKey = (recipientsByKey && recipientsByKey.length)
                  ? recipientsByKey
                  : coleadRow.keys.map((k) => ({
                      key_id: String(k.id),
                      bdx_key: k.Bdx_Key || '',
                      umr: k.UMR || '',
                      underwriter_lead: k.Lead_Underwriter || '',
                      recipients: String(emails || '').split(',').map((e) => e.trim()).filter(Boolean),
                      recipients_by_role: { broker: [] }
                    }))
                formData.append("is_co_lead", true);
                formData.append("recipients_by_key", JSON.stringify(byKey));
              }

              await CashCallService.sendEmail(formData);

              Vue.prototype.$emitter.emit("cashcall:email-sent", { id: cashCallId });
              if (isTpaApproval){
                showToast("Cash Call sent for TPA Internal Approval successfully!");
              }
              else{
                showToast("Cash Call emailed successfully!");
              }

          } catch (err) {
              console.error("Send email failed:", err);
              showToast("Error sending email", err);
          }
          showMailModal.value = false;
        }

        const emailCashCall = async (item) => {
          selectedCashCall.value = item

          if (item.Status === 'SUBMITTED') {

            let recipients = []
            try {
              const { data } = await CashCallService.getSavedRecipients(item.id)
              recipients = (data.data && data.data.data) || []   // [{ email, username, company_role_value }]
            } catch (err) {
              console.error('Failed to load saved recipients', err)
            }

            const recipientList = recipients.length
              ? recipients
                  .map(r => {
                    const displayName = r.email.split('@')[0]
                    const company = r.company_role_value || 'N/A'
                    return `<strong>${displayName} (${company})</strong>`
                  })
                  .join('<br/>')
              : '<span style="color:#999">No recipients on record</span>'

            const result = await Swal.fire({
              title: 'Resend Cash Call?',
              html: `
                <p>This Cash Call was already sent to:</p>
                ${recipientList}
                <br/><br/>
                <div style="margin-top:12px;text-align:left">
                  <label style="display:block;margin-bottom:6px">
                    <input type="radio" name="resendOption" value="same" checked />
                    &nbsp;Resend to same recipients
                  </label>
                  <label style="display:block">
                    <input type="radio" name="resendOption" value="change" />
                    &nbsp;Change recipients
                  </label>
                </div>`,
              showCancelButton: true,
              confirmButtonText: 'Resend',
              cancelButtonText: 'Cancel',
              buttonsStyling: false,
              customClass: { confirmButton: 'limeiq-btn', cancelButton: 'gray-btn' },
              focusConfirm: false,
              preConfirm: () => {
                const selected = document.querySelector('input[name="resendOption"]:checked')
                return selected ? selected.value : null
              }
            })

            if (!result.isConfirmed) return

            if (result.value === 'same') {
              // Direct resend — no recipient modal. Co-lead: only the recipients
              // listed above (those with a LimeIQ account). The saved list also
              // names people routed to support on the first send, and the
              // co-lead path would email them directly.
              const sameRecipients = isColeadRow(item) ? recipients.map((r) => r.email) : item.recipient_emails
              await handleMailSubmit({ person: sameRecipients, cashCallId: item.id })
              Swal.fire('Sent', 'Email resent successfully', 'success')
            }

            if (result.value === 'change') {
              // Open recipient selection modal
              showMailModal.value = true
            }

          } else {
            nextTick(() => {
              showMailModal.value = true
            })
          }
        }

        const disabledPaidDates = (submittedOn) => {
        return (date) => {

          if (!submittedOn) return false

          const submittedDate = new Date(submittedOn)
          const today = new Date()

          submittedDate.setHours(0,0,0,0)
          today.setHours(0,0,0,0)

          return date < submittedDate || date > today
        }
      }

        const formatCurrency = (value) => {
          if (value === null || value === undefined || value === '') return '0'
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2
          }).format(value)
        }

        const coLeadTooltip = (row) => {
          return (row.keys || [])
            .map((key) => {
              const share = key.share_percentage === null || key.share_percentage === undefined ? '—' : key.share_percentage
              const notFunded = key.funding_required === false ? ', not funded' : ''
              return `${key.Bdx_Key || key.UMR || '—'} (${share}%${notFunded})`
            })
            .join('\n')
        }

        // Splits a comma-separated string (recipient_emails, TPA, Broker, Lead
        // Underwriter) into trimmed, non-blank entries for the "(+N)" cells.
        const splitList = (value) => {
          return (value || '').split(',').map((e) => e.trim()).filter(Boolean)
        }

        // Columns whose cell shows the first value + "(+N)" (see cellList).
        const listColumns = ['TPA', 'Broker', 'Lead_Underwriter']

        // Distinct names in a TPA / Broker / Lead Underwriter value. These are
        // comma-joined twice over: the list API joins each key's value, and one
        // key can itself hold several (the create form saves e.g.
        // tpa_array.join(',')), so splitting on commas is the only way to get
        // the individual names - a name containing a comma can't be told apart.
        const cellList = (columnKey, item) => {
          return splitList(item[columnKey]).filter((name, index, all) => all.indexOf(name) === index)
        }

        const approverLabel = (approver) => `${approver.name} (${approver.email})`

        const approverTooltip = (approvers) => {
          return approvers.map(approverLabel).join('\n')
        }

        // Plain-text columns of the All Cash Calls table whose cells show the
        // full value on hover; every other text column is cut off without one.
        const bottomHoverTextColumns = ['Insured_Name']

        const formatRecipientsName = (value, broker) => {
          if (!value) return '';          

          return value
            .split(',')  // split multiple emails
            .map(email => {
              const username = email.trim().split('@')[0];
              const formattedName = username
              .split('.')
              .map(
                part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
              )
              .join(' ');

            // append broker in brackets if provided
            return broker
              ? `${formattedName} (${broker})`
              : formattedName;
                             
            })
            .join("<br />"); // each name on new line
        };

        const buildMailtoLink = (email, item) => {
          const ref = 'CC-' + item.cash_call_reference || ''
          const insured = item.Insured_Name || ''

          const subject = encodeURIComponent(`${ref} | ${insured}`)

          return `mailto:${email}?subject=${subject}`
        }

        const canChangeStatus = (item) => {
          return item.Status === "SUBMITTED";
        };



        const itemsProvider = async (context) => {
            if (!userCompanyRoleName.value && !userCompanyRoleValue.value) return []

            const { filter, filters, currentPage, perPage, sortBy, sortDesc } = context

            const [createdOnStart, createdOnEnd] = mainFilters.value.created_on || []

            const [paidOnStart, paidOnEnd] = mainFilters.value.paid_on || []

            const totalIndemnityFrom = mainFilters.value.total_indemnity && mainFilters.value.total_indemnity.from
            const totalIndemnityTo = mainFilters.value.total_indemnity && mainFilters.value.total_indemnity.to

            const paidAmountFrom = mainFilters.value.paid_amount && mainFilters.value.paid_amount.from
            const paidAmountTo = mainFilters.value.paid_amount && mainFilters.value.paid_amount.to

            const selectedId = null

            const columnFilters = {
                Status: [mainFilters.value.Status],  
                ...(!!createdOnEnd && {
                created_on_lt: format(createdOnEnd, DATE_FORMAT)
                }),
                ...(!!createdOnStart && {
                created_on_gt: format(createdOnStart, DATE_FORMAT)
                }),
                ...(totalIndemnityFrom != null && totalIndemnityFrom !== '' && {
                total_indemnity_gt: totalIndemnityFrom,
              }),
              ...(totalIndemnityTo != null && totalIndemnityTo !== '' && {
                total_indemnity_lt: totalIndemnityTo,
              }),
              ...(!!paidOnEnd && {
                paid_on_lt: format(paidOnEnd, DATE_FORMAT)
                }),
                ...(!!paidOnStart && {
                paid_on_gt: format(paidOnStart, DATE_FORMAT)
                }),
                ...(paidAmountFrom != null && paidAmountFrom !== '' && {
                  paid_amount_gt: paidAmountFrom,
              }),
              ...(paidAmountTo != null && paidAmountTo !== '' && {
                paid_amount_lt: paidAmountTo,
              }),
              ...(selectedId && {
                id: selectedId
              })
            }

            if (!!filters.length) {
                filters.forEach((filter) => {
                if (!!columnFilters[filter.key]) {
                    columnFilters[filter.key].push(filter.value)
                } else {
                    columnFilters[filter.key] = [filter.value]
                }
                })
            }

            if (directLinkBdxKey.value) {
              columnFilters['Bdx_Key'] = [directLinkBdxKey.value]
            }

            try {
                loading.value = true
                const companyRole = isSuperUser.value
                ? bdxFilters.value.companyRoleName
                : userCompanyRoleName.value
                const companyRoleValue = isSuperUser.value
                ? bdxFilters.value.companyRoleValue
                : userCompanyRoleValue.value

                const { data } = await CashCallService.list(
                companyRole,
                companyRoleValue,
                userEmail.value,
                userId.value,
                {
                    ...columnFilters,
                    page: currentPage,
                    page_size: perPage,
                    search: filter,
                    sort: sortBy ? `${sortDesc ? '-' : ''}${sortBy}` : 'created_on'
                }
                )

                 const { count, results } = data.data || {}

                total.value = count

               const items = Object.values(results).flat()

              // store full items indexed by ID (because we need to emit the full object, not the only table column fields.)
              cashCallRecordById.value = {}
              items.forEach(item => {
                cashCallRecordById.value[item.id] = item
              })

              loading.value = false

              if (selectedSubmittedId.value) {
                return items.filter(i => i.id === selectedSubmittedId.value)
              }

              if (directLinkCashCallId.value) {
                const targetId = directLinkCashCallId.value
                var action = directLinkAction.value
                const linkUrgent = directLinkUrgent.value
                const linkHighImportance = directLinkHighImportance.value

                // Clear immediately so it doesn't re-trigger on next refresh
                directLinkCashCallId.value = null
                directLinkBdxKey.value = null
                directLinkAction.value = null
                directLinkUrgent.value = false
                directLinkHighImportance.value = false

                await nextTick()

                try {
                  // Safety check — make sure items loaded and the target id exists
                  if (!items || !items.length) {
                    return items
                  }

                  // Check the target exists in the returned list
                  const targetExists = items.some(i => i && i.id === targetId)

                  if (!targetExists) {
                    return items
                  }

                  // Pull from the indexed map which is guaranteed to be populated at this point
                  const item = cashCallRecordById.value[targetId]

                  if (!item) {
                    return items
                  }

                  if (action === 'Approve') {

                    if (!isAuthorizedForCashCall(item)) {
                      Swal.fire({
                        icon: 'error',
                        title: 'Unauthorized',
                        text: 'You are not authorized to approve this cash call.',
                        buttonsStyling: false,
                        confirmButtonText: 'OK',
                        customClass: { confirmButton: 'swal2-confirm limeiq-btn' }
                      })
                      return items
                    }

                    // Trigger email modal directly with saved recipients
                    selectedCashCall.value = item
                    await handleMailSubmit({
                      person: item.recipient_emails,
                      cashCallId: item.id,
                      approvers: "",
                      isTpaApproval: false,
                      // Keep the requester's Urgent / High Importance choice on
                      // the market email that approval sends.
                      urgent: linkUrgent,
                      highImportance: linkHighImportance
                    })
                  } else if (action === 'Withdrawn') {
                    if (!isAuthorizedForCashCall(item)) {
                      Swal.fire({
                        icon: 'error',
                        title: 'Unauthorized',
                        text: 'You are not authorized to withdrawn this cash call.',
                        buttonsStyling: false,
                        confirmButtonText: 'OK',
                        customClass: { confirmButton: 'swal2-confirm limeiq-btn' }
                      })
                      return items
                    }

                    // Open the status modal pre-set to Withdrawn
                    await nextTick()
                    confirmWithdrawn(item)

                  } else {
                    // Default: open edit modal - if in PENDING
                    if (item.Status === 'PENDING') {
                      if (!isAuthorizedForCashCall(item)) {
                        Swal.fire({
                          icon: 'error',
                          title: 'Unauthorized',
                          text: 'You are not authorized to edit this cash call.',
                          buttonsStyling: false,
                          confirmButtonText: 'OK',
                          customClass: { confirmButton: 'swal2-confirm limeiq-btn' }
                        })
                        return items
                      }
                      emit('edit:item', item)
                    }
                  }

                } catch (e) {
                  console.error('Direct link: failed to auto-open cash call', e)
                }
              }
                
              return items;
            } catch (err) {
                loading.value = false
                console.error('Error fetching CashCall Records', err)
                total.value = 0
                return []
            }
        }

        const isAuthorizedForCashCall = (item) => {
          if (isSuperUser.value) return true
          return Array.isArray(item.tpa_approvers) &&
            item.tpa_approvers.some(a => a.email === userEmail.value)
        }

        const submittedItemsProvider = async (context) => {

          if (!userCompanyRoleName.value && !userCompanyRoleValue.value) return []

          const { filter, filters, currentPage, perPage, sortBy, sortDesc } = context

          const [createdOnStart, createdOnEnd] = mainFilters.value.created_on || []
          const [paidOnStart, paidOnEnd] = mainFilters.value.paid_on || []

          const totalIndemnityFrom = mainFilters.value.total_indemnity && mainFilters.value.total_indemnity.from
          const totalIndemnityTo = mainFilters.value.total_indemnity && mainFilters.value.total_indemnity.to

          const paidAmountFrom = mainFilters.value.paid_amount && mainFilters.value.paid_amount.from
          const paidAmountTo = mainFilters.value.paid_amount && mainFilters.value.paid_amount.to

          const selectedId = selectedSubmittedId.value

          const columnFilters = {
            Status: ["SUBMITTED"],

            ...(!!createdOnEnd && {
              created_on_lt: format(createdOnEnd, DATE_FORMAT)
            }),
            ...(!!createdOnStart && {
              created_on_gt: format(createdOnStart, DATE_FORMAT)
            }),

            ...(totalIndemnityFrom != null && totalIndemnityFrom !== '' && {
              total_indemnity_gt: totalIndemnityFrom,
            }),
            ...(totalIndemnityTo != null && totalIndemnityTo !== '' && {
              total_indemnity_lt: totalIndemnityTo,
            }),

            ...(!!paidOnEnd && {
              paid_on_lt: format(paidOnEnd, DATE_FORMAT)
            }),
            ...(!!paidOnStart && {
              paid_on_gt: format(paidOnStart, DATE_FORMAT)
            }),

            ...(paidAmountFrom != null && paidAmountFrom !== '' && {
              paid_amount_gt: paidAmountFrom,
            }),
            ...(paidAmountTo != null && paidAmountTo !== '' && {
              paid_amount_lt: paidAmountTo,
            }),

            ...(selectedId && { id: selectedId })
          }

          if (!!filters.length) {
            filters.forEach((f) => {
              if (!!columnFilters[f.key]) {
                columnFilters[f.key].push(f.value)
              } else {
                columnFilters[f.key] = [f.value]
              }
            })
          }

          try {
            actionTableLoading.value = true

            const companyRole = isSuperUser.value
              ? bdxFilters.value.companyRoleName
              : userCompanyRoleName.value

            const companyRoleValue = isSuperUser.value
              ? bdxFilters.value.companyRoleValue
              : userCompanyRoleValue.value

            const { data } = await CashCallService.list(
              companyRole,
              companyRoleValue,
              userEmail.value,
              userId.value,
              {
                ...columnFilters,
                page: currentPage,
                page_size: perPage,
                search: filter,
                sort: sortBy
                  ? `${sortDesc ? '-' : ''}${sortBy}`
                  : 'created_on'
              }
            )

            const { count, results } = data.data || {}

            submittedTotal.value = count

            const items = Object.values(results).flat()

            actionTableLoading.value = false

            return items

          } catch (err) {
            actionTableLoading.value = false
            submittedTotal.value = 0
            return []
          }
        }

        // A co-lead draft may be saved with its UMR shares not yet totalling 100%
        // (LIQ-2414). It must not be sent like that - the market would get wrong
        // per-UMR amounts and, once SUBMITTED, it would reach the export's Cash
        // Call Reconciliation step. Same rule the send endpoint enforces
        // (co_lead_share_error); every key counts, funded or not.
        const isCoLeadShareIncomplete = (item) => {
          if (!item || !item.is_co_lead) return false
          const total = (item.keys || [])
            .reduce((sum, key) => sum + (Number(key.share_percentage) || 0), 0)
          return Math.round(total * 100) / 100 !== 100
        };

        const isEmailDisabled = (item) => {
          if (isReadOnlyRole.value) return true
          if (isCoLeadShareIncomplete(item)) return true
          // PARTIALLY PAID must be blocked exactly like PAID: sending a cash call
          // sets it back to SUBMITTED on the backend, so emailing a finished one
          // would reopen it and lose its terminal status.
          return ["CANCELLED", "REJECTED", "PAID", "PARTIALLY PAID", "PENDING"].includes(item.Status)
        };

        // Only the share rule explains itself: the status rules are self-evident
        // from the row's own Status column.
        const emailIconTooltip = (item) =>
          isCoLeadShareIncomplete(item)
            ? 'Co-lead UMR shares must total 100% before this cash call can be sent'
            : '';

        const isEditDisabled = (item) => {
          if (isReadOnlyRole.value) return true
          // PARTIALLY PAID is terminal like PAID (co-lead: some UMRs paid, the
          // rest rejected), so it must not reopen for editing either.
          return ["CANCELLED", "REJECTED", "PAID", "PARTIALLY PAID", "SUBMITTED", "PENDING"].includes(item.Status)
        };

        const emailIconStyle = (item) => ({
          opacity: isEmailDisabled(item) ? 0.3 : 1,
          cursor: isEmailDisabled(item) ? "not-allowed" : "pointer",
          pointerEvents: isEmailDisabled(item) ? "none" : "auto"
        });

        const editIconStyle = (item) => ({
          opacity: isEditDisabled(item) ? 0.3 : 1,
          cursor: isEditDisabled(item) ? "not-allowed" : "pointer",
          pointerEvents: isEditDisabled(item) ? "none" : "auto"
        });

        const handleEdit = async (id) => {
          const fullItem = cashCallRecordById.value[id]      

          console.log('full item')
          console.log(fullItem)

          await nextTick()
          emit('edit:item', fullItem)
        }

        const toggleSubmitted = (id) => {
          selectedSubmittedId.value =
          selectedSubmittedId.value === id ? null : id
        }


        const isDeleteDisabled = (item) => {
          if (isReadOnlyRole.value) return true
          return item.Status !== "DRAFT";   // Only allow delete when STATUS = DRAFT
        };

        const deleteIconStyle = (item) => ({
          opacity: isDeleteDisabled(item) ? 0.3 : 1,
          cursor: isDeleteDisabled(item) ? "not-allowed" : "pointer",
          pointerEvents: isDeleteDisabled(item) ? "none" : "auto",
        });

        const handleDelete = (item) => {
          Swal.fire({
            title: "Are you sure?",
            text: "This will permanently delete the Cash Call record.",
            
            showCancelButton: true,
            confirmButtonColor: "#1c4a50",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "DELETE",
            cancelButtonText: "CANCEL",
            buttonsStyling: false,
            customClass: {
              popup: "swal2-popup",
              confirmButton: "swal2-confirm",
              cancelButton: "swal2-cancel"
            }
          }).then(async (result) => {
            if (result.isConfirmed) {
              try {
                await CashCallService.deleteCashCall(item.id); // backend API call

                Swal.fire({
                  icon: "success",
                  title: "Deleted!",
                  text: "Cash Call was deleted successfully.",
                  icon: "success",
                  buttonsStyling: false,
                  confirmButtonText: "OK",
                  customClass: {
                    confirmButton: "swal2-confirm"
                  }
                });

                // refresh table
                handleRefresh();

              } catch (err) {
                console.error("Delete failed:", err);

                Swal.fire({
                  icon: "error",
                  title: "Error!",
                  text: "Unable to delete Cash Call. Please try again.",
                  icon: "error",
                  buttonsStyling: false,
                  confirmButtonText: "OK",
                  customClass: {
                    confirmButton: "swal2-confirm"
                  }
                });
              }
            }
          });
        };

        const confirmStatusChange = async (item, newStatus) => {
          selectedStatusItem.value = item
          statusMode.value = newStatus

          paymentType.value = 'FULL'
          paidIndemnity.value = ''
          statusComment.value = ''
          const today = new Date()
          paidDate.value = today.toLocaleDateString('en-CA')
          receivedDate.value = ''
          feeBreakdown.value = {}
          statusErrors.value = {}
          // Reset co-lead per-key payment inputs.
          coleadPayInputs.value = {}
          coleadPayErrors.value = {}
          coleadPaySavingKeyId.value = null
          // Accordion: every key starts COLLAPSED so the modal opens compact and
          // the user explicitly picks the UMR they want to pay/reject.
          coleadKeyExpanded.value = {}

          showStatusModal.value = true
        }

        const confirmWithdrawn = async (item) => {
          selectedStatusItem.value = item
          statusMode.value = 'WITHDRAWN'
          statusComment.value = ''
          statusErrors.value = {}
          showStatusModal.value = true
        }

        const submitStatusChange = async () => {
          const item = selectedStatusItem.value
          if (!item) return

          statusErrors.value = {}

          const observerValid = await statusObserver.value.validate()
          if (!observerValid) return

          let paidIndemnityValue = null
          let paidFee = null
          let paidFeeBreakdown = null
          let paidDateValue = null

          // ---------- PAID VALIDATIONS ----------
          if (statusMode.value === 'PAID') {
            if (paymentType.value === 'PARTIAL') {
              const indemnity = paidIndemnity.value
                ? parseFloat(String(paidIndemnity.value).replace(/,/g, ''))
                : 0

              // Ceilings account for what earlier installments already covered,
              // so repeated payments cannot exceed the requested total.
              const indemnityRemaining = Number(item.Total_Indemnity_Amount || 0) -
                Number(item.Paid_Indemnity || 0)

              // Once the indemnity is fully covered the outstanding balance is
              // fees only, so a zero/blank indemnity is valid - requiring one
              // would block the final fees-only payment.
              if (indemnityRemaining <= 0.01 && (isNaN(indemnity) || indemnity <= 0)) {
                paidIndemnityValue = 0
              } else if (isNaN(indemnity) || indemnity <= 0) {
                statusErrors.value.paidIndemnity = 'Valid paid indemnity is required'
              } else if (indemnity > indemnityRemaining + 0.01) {
                statusErrors.value.paidIndemnity = Number(item.Paid_Indemnity || 0) > 0
                  ? `Paid Indemnity cannot exceed the outstanding amount (${formatCurrency(indemnityRemaining)})`
                  : 'Paid Indemnity cannot exceed requested amount'
              } else {
                paidIndemnityValue = indemnity
              }

              const breakdown = {}
              let totalFee = 0

              activeFeeFields.value.forEach((f) => {
                const raw = feeBreakdown.value[f.key]
                const val = raw ? parseFloat(String(raw).replace(/,/g, '')) : 0

                // f.requested is already reduced to what is OUTSTANDING on this
                // fee (requested less what earlier installments paid).
                if (!isNaN(val) && val > f.requested + 0.01) {
                  statusErrors.value[f.key] = Number(item[f.paidField] || 0) > 0
                    ? `${f.label} cannot exceed the outstanding amount (${formatCurrency(f.requested)})`
                    : `${f.label} cannot exceed requested amount`
                } else {
                  breakdown[f.key] = isNaN(val) ? 0 : val
                  totalFee += isNaN(val) ? 0 : val
                }
              })

              // Cap the fee TOTAL against what is still outstanding across all
              // fees, not the original requested total.
              const feeRemaining = Number(item.Total_Fee_Amount || 0) -
                Number(item.Paid_Fee || 0)
              if (totalFee > feeRemaining + 0.01) {
                statusErrors.value.totalFee = Number(item.Paid_Fee || 0) > 0
                  ? `Total Paid Fee cannot exceed the outstanding fee amount (${formatCurrency(feeRemaining)})`
                  : 'Total Paid Fee cannot exceed requested fee amount'
              } else {
                paidFee = totalFee
                paidFeeBreakdown = breakdown
              }
            } else {
              // FULL settles whatever is STILL OUTSTANDING, not the original
              // requested figures - on a cash call part-paid by earlier
              // installments those would double-count what has already been
              // received. With no prior payments this is the full request.
              paidIndemnityValue = Math.max(0,
                parseFloat(item.Total_Indemnity_Amount || 0) - Number(item.Paid_Indemnity || 0)
              )
              paidFee = Math.max(0,
                parseFloat(item.Total_Fee_Amount || 0) - Number(item.Paid_Fee || 0)
              )
            }

            // Paid date validation
            if (paidDate.value) {

            // Compare CALENDAR DAYS as YYYY-MM-DD strings, not Date objects.
            // paidDate.value is a date-only string ("2026-09-12") which JS parses
            // as UTC midnight, while submitted_on is a full timestamp parsed as
            // LOCAL time. Normalising those two with setHours() mixes the bases
            // and shifted the paid date back a day in negative-offset timezones,
            // producing a false "Paid Date cannot be earlier than Submitted Date"
            // for a payment made on the very same day. localDay() renders every
            // value in the browser's own timezone so all three are comparable.
            // (The co-lead payment flow has no such check, which is why it
            // accepted the same dates that were rejected here.)
            const localDay = (d) => {
              const dt = (d instanceof Date) ? d : new Date(d)
              if (isNaN(dt.getTime())) return ''
              const y = dt.getFullYear()
              const m = String(dt.getMonth() + 1).padStart(2, '0')
              const day = String(dt.getDate()).padStart(2, '0')
              return `${y}-${m}-${day}`
            }
            // Already a plain calendar date - take it as-is, no parsing.
            const paid = /^\d{4}-\d{2}-\d{2}$/.test(String(paidDate.value))
              ? String(paidDate.value)
              : localDay(paidDate.value)
            const submitted = localDay(item.submitted_on)
            const today = localDay(new Date())

            if (submitted && paid < submitted) {
              statusErrors.value.paidDate = 'Paid Date cannot be earlier than Submitted Date'
            } else if (paid > today) {
              statusErrors.value.paidDate = 'Paid Date cannot be a future date'
            } else {
              paidDateValue = paidDate.value
            }

            } else {
            paidDateValue = null
            }
          }

          // Received date (Cash Call Received Date) - optional; if present it
          // can't be after the Paid to Insured Date.
          let receivedDateValue = null
          if (statusMode.value === 'PAID' && receivedDate.value) {
            if (paidDate.value && receivedDate.value > paidDate.value) {
              statusErrors.value.receivedDate = 'Cash Call Received Date cannot be after Paid to Insured Date'
            } else {
              receivedDateValue = receivedDate.value
            }
          }

          // If any custom validation errors exist, stop
          if (Object.keys(statusErrors.value).length > 0) return

          // ---------- Final payment (PARTIAL only) ----------
          // A PARTIAL payment does not settle the cash call in full, so ask
          // whether it is nonetheless the FINAL payment. "No, awaiting funds"
          // records the installment and leaves the cash call SUBMITTED so
          // further payments can be added; "Yes" closes it as PAID with only
          // what was collected (the shortfall is written off). A FULL payment
          // settles everything, so no prompt is shown.
          //
          // Mirrors the co-lead per-UMR flow. The status stays PAID either way -
          // PARTIALLY PAID is a co-lead-only concept and introducing it here
          // would drop the cash call off the BDX export.
          let isFinalPayment = true
          if (statusMode.value === 'PAID' && paymentType.value === 'PARTIAL') {
            const requestedTotal = Number(item.Total_Indemnity_Amount || 0) +
              Number(item.Total_Fee_Amount || 0)
            // Payments already recorded against this cash call (installments
            // added before this one).
            const alreadyPaid = Number(item.Paid_Amount || 0)
            const thisPayment = (paidIndemnityValue || 0) + (paidFee || 0)
            const shortfall = requestedTotal - alreadyPaid - thisPayment

            if (shortfall > 0.01) {
              const answer = await vm.$bvModal.msgBoxConfirm(
                `Is this the final payment for this cash call? Choosing "Yes" closes it and writes off the remaining ${formatCurrency(shortfall)}.`,
                {
                  title: 'Partial Payment',
                  okTitle: 'Yes, final payment',
                  cancelTitle: 'No, awaiting funds',
                  okVariant: 'primary',
                  centered: true
                }
              )
              // Cancelling (Esc / backdrop) returns null - abort so the user can
              // re-confirm rather than silently closing the cash call.
              if (answer === null) return
              isFinalPayment = answer === true
            }
          }

          // ---------- API ----------
          try {
            await CashCallService.updateStatus(item.id, {
              status: statusMode.value,
              status_comment: statusComment.value,
              // False when the user said more payments are coming - the backend
              // records the installment but keeps the cash call SUBMITTED.
              is_final: isFinalPayment,
              // Lets the backend tell a user-entered split (PARTIAL) from the
              // requested figures (FULL) when storing the payment record.
              payment_type: paymentType.value,
              ...(paidIndemnityValue !== null && { paid_indemnity: paidIndemnityValue }),
              ...(paidFee !== null && { paid_fee: paidFee }),
              ...(paidDateValue !== null && { paid_date: paidDateValue }),
              ...(receivedDateValue !== null && { received_date: receivedDateValue }),
              ...(paidFeeBreakdown && {
                paid_tpa_fees_amount: paidFeeBreakdown.tpa_fees_amount == null || undefined ? 0 : paidFeeBreakdown.tpa_fees_amount,
                paid_adjuster_fee_amount: paidFeeBreakdown.adjuster_fee_amount == null || undefined ? 0 :paidFeeBreakdown.adjuster_fee_amount,
                paid_defence_fees_amount: paidFeeBreakdown.defence_fees_amount == null || undefined ? 0 :paidFeeBreakdown.defence_fees_amount,
                paid_expenses_amount: paidFeeBreakdown.expenses_amount == null || undefined ? 0 : paidFeeBreakdown.expenses_amount,
                paid_attorney_coverage_amount: paidFeeBreakdown.attorney_coverage_amount == null || undefined ? 0 : paidFeeBreakdown.attorney_coverage_amount
              })
            })

            showStatusModal.value = false

            // An interim installment ("No, awaiting funds") leaves the cash call
            // SUBMITTED, so "marked as PAID" would be wrong. Acknowledge it with
            // the same quiet toast the co-lead flow uses and save the success
            // popup for a cash call that has actually been settled.
            if (!isFinalPayment) {
              showToast('Payment recorded')
            } else {
              Swal.fire({
                icon: 'success',
                title: 'Updated',
                text: `Cash Call marked as ${statusMode.value}`,
                buttonsStyling: false,
                confirmButtonText: 'OK',
                customClass: {
                  confirmButton: 'swal2-confirm limeiq-btn'
                }
              })
            }

            handleRefresh()
          } catch (err) {
            console.error(err)

            Swal.fire({
              icon: 'error',
              title: 'Error',
              // The backend's reason (e.g. the comment is too long), not just
              // "Unable to update status".
              text: apiErrorMessage(err, 'Unable to update status'),
              buttonsStyling: false,
              confirmButtonText: 'OK',
              customClass: {
                confirmButton: 'swal2-confirm'
              }
            })
          }
        }

        const confirmStatusChange1 = async (item, newStatus) => {
          let paidIndemnity = null;
          let paidFee = null;
          let statusComment = null;
          let statusFinal = null;
          let paidDate = null;
          let paidFeeBreakdown = null;


          // PAID → show input modal to get the amount paid.
          if (newStatus === "PAID") {
            const formatMoney = (n) =>
                  new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD"
                  }).format(n || 0);
            const result = await Swal.fire({
              title: "Mark as Paid",
              width: '900px',
              html: `
                <div class="limeiq-modal">

                  <div class="limeiq-summary">
                    <div>Total Requested Amount: <span class="limeiq-money">${formatMoney(item.Cash_Call_Request_Amount)}</span></div>
                    

                    <div class="limeiq-breakdown">
                      <span>Indemnity: ${formatMoney(item.Total_Indemnity_Amount)}</span>
                      <span>Fees: ${formatMoney(item.Total_Fee_Amount)}</span>
                    </div>
                  </div>

                  <div class="limeiq-field">
                    <label>Payment Amount</label>
                    <select id="paymentType" class="limeiq-input">
                      <option value="FULL" selected>Full</option>
                      <option value="PARTIAL">Partial</option>
                    </select>
                  </div>

                  <div id="partialSection" class="limeiq-partial-section" style="display:none">

                    <div class="limeiq-field">
                      <label>Paid Indemnity</label>
                      <input id="paidIndemnity" class="limeiq-input" placeholder="Paid indemnity" />
                    </div>

                    <div class="limeiq-field" id="paidFeeSection" style="margin-top:15px !important">
                      <label>Paid Fees Breakdown</label>

                      <div class="limeiq-fee-scroll">
                        <div id="feeBreakdownContainer" class="limeiq-fee-grid">                          
                        </div>
                      </div>

                      <div class="limeiq-fee-total">
                        <span>Total Paid Fees: &nbsp;</span>
                        <span id="paidFeeTotal">$0.00</span>
                      </div>
                    </div>

                  </div>

                  <div class="limeiq-field">
                    <label>Paid Date</label>
                    <input id="paidDate" type="date" class="limeiq-input" />
                  </div>

                  <div class="limeiq-field">
                    <label>Comment</label>
                    <textarea id="comment" class="limeiq-textarea" placeholder="Enter comment…"></textarea>
                  </div>

                </div>
              `,

              showCancelButton: true,
              confirmButtonText: "Confirm Paid",
              cancelButtonText: "Cancel",
              buttonsStyling: false,
              customClass: {
                confirmButton: "swal2-confirm limeiq-btn",
                cancelButton: "swal2-cancel gray-btn"
              },
              onOpen: () => { 
                const feeFieldConfig = [
                  {
                    key: "tpa_fees_amount",
                    label: "TPA Fees",
                    requested: item.TPA_Fees_Amount
                  },
                  {
                    key: "adjuster_fee_amount",
                    label: "Adjusters Fees",
                    requested: item.Adjusters_Fees_Amount
                  },
                  {
                    key: "defence_fees_amount",
                    label: "Defence Fees",
                    requested: item.Defence_Fees_Amount
                  },
                  {
                    key: "expenses_amount",
                    label: "Expenses",
                    requested: item.Expenses_Amount
                  },
                  {
                    key: "attorney_coverage_amount",
                    label: "Attorney Coverage",
                    requested: item.Attorney_Coverage_Amount
                  }
                ];

                const paymentType = document.getElementById("paymentType")
                const partial = document.getElementById("partialSection")

                const container = document.getElementById("feeBreakdownContainer");

                const activeFeeFields = feeFieldConfig.filter(f => Number(f.requested) > 0);

                if(activeFeeFields.length == 0)
                {
                  document.getElementById("paidFeeSection").style.display = 'none';
                }  
                else {
                  document.getElementById("paidFeeSection").style.display = 'block';
                }

                container.innerHTML = activeFeeFields.map(f => `
                  <div class="limeiq-fee-item">
                    <label class="limeiq-sub-label">
                      ${f.label} (${formatMoney(f.requested)})
                    </label>
                    <input 
                      id="paid_${f.key}" 
                      class="limeiq-input limeiq-input-full" 
                      placeholder="0.00"
                    />
                  </div>
                `).join("");


                document.getElementById("paidDate").valueAsDate = new Date()

                // --- helper: currency-like input formatting (commas, 2 decimals) ---
                const attachMoneyInput = (id, onAfter) => {
                  const el = document.getElementById(id);
                  if (!el) return;

                  el.addEventListener("input", () => {
                    const raw = el.value;
                    const cursor = el.selectionStart ? el.selectionStart : raw.length;

                    let cleaned = raw.replace(/[^0-9.]/g, "");

                    const firstDot = cleaned.indexOf(".");
                    if (firstDot !== -1) {
                      cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, "");
                    }

                    let intPart = cleaned;
                    let decPart = "";
                    let hasDot = false;

                    if (cleaned.includes(".")) {
                      [intPart, decPart] = cleaned.split(".");
                      hasDot = true;
                    }

                    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                    let formatted = formattedInt;

                    if (hasDot) {
                      formatted += "." + (decPart ? decPart : "").slice(0, 2);
                    }

                    el.value = formatted;

                    const diff = formatted.length - raw.length;
                    const next = Math.max(0, cursor + diff);
                    el.setSelectionRange(next, next);

                    if (typeof onAfter === "function") onAfter();
                  });
                };

                const readMoney = (id) => {
                  const el = document.getElementById(id);
                  if (!el || !el.value) return 0;
                  const v = parseFloat(el.value.replace(/,/g, ""));
                  return isNaN(v) ? 0 : v;
                };

                const formatMoneyText = (n) =>
                  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

                  const readMoneyDynamic = (id) => {
                    const el = document.getElementById(id);
                    if (!el || !el.value) return 0;
                    const v = parseFloat(el.value.replace(/,/g, ""));
                    return isNaN(v) ? 0 : v;
                  };

                  const updateFeeTotal = () => {
                    let total = 0;

                    activeFeeFields.forEach(f => {
                      total += readMoneyDynamic(`paid_${f.key}`);
                    });

                    document.getElementById("paidFeeTotal").innerText =
                      new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD"
                      }).format(total);
                  };

                  activeFeeFields.forEach(f => {
                    attachMoneyInput(`paid_${f.key}`, updateFeeTotal);
                  });


                paymentType.addEventListener("change", () => {
                  partial.style.display = paymentType.value === "PARTIAL" ? "block" : "none";
                });

                // Attach formatters
                attachMoneyInput("paidIndemnity");

                // Fee components + auto total
                attachMoneyInput("paidTPAFees", updateFeeTotal);
                attachMoneyInput("paidAdjusterFees", updateFeeTotal);
                attachMoneyInput("paidDefenceFees", updateFeeTotal);
                attachMoneyInput("paidExpenses", updateFeeTotal);
                attachMoneyInput("paidAttorneyCoverage", updateFeeTotal);

                updateFeeTotal();
              },

              preConfirm: () => {
                const paymentType = document.getElementById("paymentType").value
                const comment = document.getElementById("comment").value;
                const paidDateRaw = document.getElementById("paidDate").value; // yyyy-mm-dd or ""

                if (!comment) {
                  Swal.showValidationMessage("Comment is required")
                  return false
                }

                let value = parseFloat(item.Total_Indemnity_Amount);
                let valueFee = parseFloat(item.Total_Fee_Amount);

                if (paymentType === "PARTIAL") {

                const rawIndemnity = document.getElementById("paidIndemnity").value;
                const value = rawIndemnity
                  ? parseFloat(rawIndemnity.replace(/,/g, ""))
                  : 0;

                if (isNaN(value) || value <= 0) {
                  Swal.showValidationMessage("Valid paid indemnity is required");
                  return false;
                }

                if (value > item.Total_Indemnity_Amount) {
                  Swal.showValidationMessage("Paid Indemnity cannot exceed requested amount");
                  return false;
                }

                const feeFieldConfig = [
                  {
                    key: "tpa_fees_amount",
                    label: "TPA Fees",
                    requested: item.TPA_Fees_Amount
                  },
                  {
                    key: "adjuster_fee_amount",
                    label: "Adjusters Fees",
                    requested: item.Adjusters_Fees_Amount
                  },
                  {
                    key: "defence_fees_amount",
                    label: "Defence Fees",
                    requested: item.Defence_Fees_Amount
                  },
                  {
                    key: "expenses_amount",
                    label: "Expenses",
                    requested: item.Expenses_Amount
                  },
                  {
                    key: "attorney_coverage_amount",
                    label: "Attorney Coverage",
                    requested: item.Attorney_Coverage_Amount
                  }
                ];

                const fee_breakdown = {};
                let totalFee = 0;

                for (const f of feeFieldConfig) {

                  if (Number(f.requested) <= 0) continue;

                  const el = document.getElementById(`paid_${f.key}`);

                  const fieldValue = el && el.value
                    ? parseFloat(el.value.replace(/,/g, ""))
                    : 0;

                  if (fieldValue > Number(f.requested)) {
                    Swal.showValidationMessage(
                      `${f.label} cannot exceed requested amount`
                    );
                    return false;
                  }

                  fee_breakdown[f.key] = fieldValue;
                  totalFee += fieldValue;
                }

                if (totalFee > item.Total_Fee_Amount) {
                  Swal.showValidationMessage(
                    "Total Paid Fee cannot exceed requested fee amount"
                  );
                  return false;
                }

                const paidDateRaw = document.getElementById("paidDate").value;
                const comment = document.getElementById("comment").value;

                return {
                  value,
                  valueFee: totalFee,
                  fee_breakdown,
                  comment,
                  paid_date: paidDateRaw || null
                };
                }



                // ----- Paid date validation (optional) -----
                let paidDate = null;

                if (paidDateRaw) {

                  paidDate = new Date(paidDateRaw);
                  const submittedDate = new Date(item.submitted_on);
                  const today = new Date();

                  // normalize all to local midnight
                  paidDate.setHours(0, 0, 0, 0);
                  submittedDate.setHours(0, 0, 0, 0);
                  today.setHours(0, 0, 0, 0);

                  // ---- Submitted date validation ----
                  if (paidDate < submittedDate) {
                    Swal.showValidationMessage(
                      "Paid Date cannot be earlier than Submitted Date"
                    );
                    return false;
                  }

                  // ---- Today validation (no future dates) ----
                  if (paidDate > today) {
                    Swal.showValidationMessage(
                      "Paid Date cannot be a future date"
                    );
                    return false;
                  }
                }

                return { value, valueFee, comment, paid_date: paidDateRaw || null };
              }
            });

            if (!result) return;

            paidIndemnity = result.value.value;
            paidFee = result.value.valueFee;
            paidFeeBreakdown = result.value.fee_breakdown || null;
            statusComment = result.value.comment;
            paidDate = result.value.paid_date;
            statusFinal = 'PAID'
          } 
          //  NON-PAID (cancelled / rejected)
          else {
            const result = await Swal.fire({
              title: `Update Status`,
              html: `<div class="text-left" style="margin-top:30px;font-family:'Segoe UI Regular',sans-serif;">
                      <label style="font-weight:bold; color:#F54927">
                        <input type="radio" name="status" value="REJECTED" checked /> REJECTED
                      </label>&nbsp;&nbsp;&nbsp;
                      <label style="font-weight:bold; color:#F54927">
                        <input type="radio" name="status" value="CANCELLED" /> CANCELLED
                      </label>
                      <textarea id="comment" class="swal2-textarea mt-2" placeholder="Comment"></textarea>
                    </div>`,
              preConfirm: () => {
                const status = document.querySelector('input[name="status"]:checked').value
                const comment = document.getElementById("comment").value

                if (!comment) {
                  Swal.showValidationMessage("Comment is required")
                  return false
                }

                return { status, comment }
              },
            showCancelButton: true
            });

            if (!result.isConfirmed) return;
            statusComment = result.value.comment;
            statusFinal = result.value.status;
          }

          //  API call (single place)
          try {
            await CashCallService.updateStatus(item.id, {
              status: statusFinal,
              status_comment: statusComment,
              ...(paidIndemnity !== null && { paid_indemnity: paidIndemnity }),
              ...(paidFee !== null && { paid_fee: paidFee }),
              ...(paidDate !== null && { paid_date: paidDate }),
              ...(paidFeeBreakdown && {
                paid_tpa_fees_amount: paidFeeBreakdown.tpa_fees_amount == null || undefined ? 0 : paidFeeBreakdown.tpa_fees_amount,
                paid_adjuster_fee_amount: paidFeeBreakdown.adjuster_fee_amount == null || undefined ? 0 :paidFeeBreakdown.adjuster_fee_amount,
                paid_defence_fees_amount: paidFeeBreakdown.defence_fees_amount == null || undefined ? 0 :paidFeeBreakdown.defence_fees_amount,
                paid_expenses_amount: paidFeeBreakdown.expenses_amount == null || undefined ? 0 : paidFeeBreakdown.expenses_amount,
                paid_attorney_coverage_amount: paidFeeBreakdown.attorney_coverage_amount == null || undefined ? 0 : paidFeeBreakdown.attorney_coverage_amount
              })
            });

            Swal.fire({
              icon: "success",
              title: "Updated",
              text: `Cash Call marked as ${newStatus}`,
              buttonsStyling: false,
              confirmButtonText: "OK",
              customClass: {
                confirmButton: "swal2-confirm limeiq-btn"
              }
            });

            handleRefresh();
          } catch (err) {
            console.error(err);

            Swal.fire({
              icon: "error",
              title: "Error",
              text: "Unable to update status",
              buttonsStyling: false,
              confirmButtonText: "OK",
              customClass: {
                confirmButton: "swal2-confirm"
              }
            });
          }
        };



        const tableTbodyTrClass = (item) => {
          if (!item) return ''

          if (item.id === selectedSubmittedId.value) {
            return 'bg-light text-dark'
          }

          return ''
        }


        const handleRefresh = async () => {
          await nextTick()

          if (dataTableRef.value) {
            dataTableRef.value.handleRefresh()
          }

          if (submittedTableRef.value) {
            submittedTableRef.value.handleRefresh()
          }

        }

        // --------------------------------------------
        // Currency formatter (reuse existing if you want)
        // --------------------------------------------
        const fmt = (v) => {
          if (v === null || v === undefined || v === '') return '—';
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2
          }).format(v);
        };


        // --------------------------------------------
        // Section helpers for formatting
        // --------------------------------------------
        const sectionBox = (title, value) => ({
          style: 'section',
          table: {
            widths: ['100%'],
            body: [
              [{ text: title, bold: true }],
              [{ text: value || '', margin: [5, 5] }]
            ]
          },
          layout: 'lightHorizontalLines',
          margin: [0, 10, 0, 10]
        });

        const sectionBoxGrid = (title, rows) => ([
          { text: title, bold: true, margin: [0, 10, 0, 4] },
          {
            table: {
              widths: ['35%', '65%'],
              body: rows.map(([label, value]) => [
                { text: label, bold: true },
                value
              ])
            },
            layout: 'lightHorizontalLines'
          }
        ]);

        const generateDocDefinition = (row, person) => {
          const logoBase64 = 'data:image/png;base64,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'; 

          const normalizeRecipients = (value) => {
            if (!value) return [];

            if (Array.isArray(value)) return String(value).split(",");

            return value;
          };
          
          const val = normalizeRecipients(person)

          const safeRecipients = (Array.isArray(val)) ? val.join(",") : val; 

            const boxLayout = {
            hLineWidth(i, node) { return 1; },
            vLineWidth(i, node) { return 1; },
            hLineColor(i, node) { return '#000'; },
            vLineColor(i, node) { return '#000'; },
            paddingLeft(i, node) { return 5; },
            paddingRight(i, node) { return 5; },
            paddingTop(i, node) { return 4; },
            paddingBottom(i, node) { return 4; }
          };

          const tightBoxLayout = {
            hLineWidth: () => 1,
            vLineWidth: () => 1,
            hLineColor: () => "#000",
            vLineColor: () => "#000",

            paddingTop: () => 2,
            paddingBottom: () => 2,
            paddingLeft: () => 4,
            paddingRight: () => 4
          };


          const line = {
                  canvas: [
                      { type: "line", x1: 0, y1: 0, x2: 520, y2: 0, lineWidth: 1 }
                  ],
                  margin: [0, 5]
              };

              // An email address is one long token with no spaces, so pdfMake
              // has nowhere to wrap it and lets it run past its column - which
              // made "Recipient Email" overlap the "Requestor Email" label.
              // Insert zero-width spaces after @ . _ - so it can break cleanly
              // (they render as nothing and are not part of the address text).
              const addBreaks = (s) => String(s).replace(/([@._-])/g, '$1​');

              // Values arrive either as a plain string/number OR as a pdfMake
              // object ({ text, style, ... }). Only transform real strings -
              // String()-ing an object yields "[object Object]". Objects are
              // returned with just their .text made breakable, so styling is
              // preserved.
              const breakable = (value) => {
                if (value === null || value === undefined || value === '') return '';
                if (typeof value === 'object') {
                  return (typeof value.text === 'string')
                    ? { ...value, text: addBreaks(value.text) }
                    : value;
                }
                return addBreaks(value);
              };

              const labelValue = (label, value) => {
                  const v = breakable(value);
                  const isEmpty = v === '' || v === null || v === undefined;
                  return {
                      columns: [
                          // Narrower label leaves usable room for the value
                          // inside the 50%-wide half-column.
                          { width: 95, text: label, bold: true },
                          isEmpty
                            ? { width: "*", text: "—" }
                            : (typeof v === 'object'
                                ? { width: "*", ...v }
                                : { width: "*", text: v })
                      ],
                      columnGap: 6,
                      margin: [0, 2]
                  };
              };

              const twoCol = (labelL, valL, labelR, valR) => {
                  return {
                      columns: [
                          { width: "50%", columns: [labelValue(labelL, valL)] },
                          { width: "50%", columns: [labelValue(labelR, valR)] }
                      ]
                  };
              };

              // Yes/No answers arrive as real booleans or, for older rows /
              // CharField-backed Authority_Scope, as the strings "True"/"False".
              const isYes = (v) => {
                if (typeof v === "boolean") return v;
                if (v === null || v === undefined || v === "") return false;
                return !["false", "0", "no"].includes(String(v).trim().toLowerCase());
              };

              const padComments = (text, targetLength = 200) => {
                if (!text) text = "";

               // --- If text exceeds target length → trim and add "..." ---
                if (text.length > targetLength) {
                  const trimmed = text.substring(0, targetLength - 3); // keep room for "..."
                  return trimmed + "...";
                }

                // Determine how many newline paddings we need
                const padSize = targetLength - text.length;

                // Add a newline every ~50 chars of padding
                const numberOfLines = Math.ceil(padSize / 50);

                return text + "\n".repeat(numberOfLines);
              }

              const resolveCreatedByEmail = (row) => {
                return String(
                  row.Created_By_Email || row.Created_By || ""
                );
              };

              const formatMoney = (n) =>
                  new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD"
                  }).format(n || 0);

              const formatRecipients = (value) => {
                if (!value) return "";
                return value.toUpperCase()
                  .split(",")
                  .map(email => `${email}`)
                  .join("\n");
              }

              const formatRecipientsName = (value) => {

                if (!value) return '';

                return value
                  .split(',')  // split multiple emails
                  .map(email => {
                    const username = email.split('@')[0];
                    return username
                      .split('.')
                      .map(
                        part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
                      )
                      .join(' ');
                  })
                  .join("\n"); // each name on new line
              };

              const uploadedDocTypes = new Set(
                (row.supporting_documents || []).map(d => d.document_type)
              )

              const docStatus = (type) => ({
                text: uploadedDocTypes.has(type) ? "Attached" : "Not Attached",
                style: "value",
                color: uploadedDocTypes.has(type) ? "black" : "#999"
              })

              const docDefinition = {
                  pageMargins: [30, 20, 30, 30],
                  styles: {
                    headerTitle: {
                      fontSize: 18,
                      bold: true,
                      alignment: "center",
                      margin: [0,2, 0, 2],
                    },

                    sectionHeader: {
                      fontSize: 9,
                      bold: true,
                      margin: [0, 2, 0, 2],
                    },

                    label: {
                      fontSize: 8,
                      bold: true,
                      margin: [0, 1, 0, 1],
                    },

                    label1: {
                      fontSize: 6,
                      bold: true,
                      margin: [0, 1, 0, 1],
                    },

                    value: {
                      fontSize: 8,
                      margin: [0, 1, 0, 1],
                    },

                    amountBarLabel: {
                      fontSize: 9,
                      bold: true,
                      fillColor: '#FFF34D',
                    },

                    amountBarValue: {
                      fontSize: 9,
                      bold: true,
                      fillColor: '#FFF34D',
                      alignment: "right"
                    }
                  },
                  defaultStyle: {
                    fontSize: 9
                  },
                  content: [
                      /* HEADER */
                      {
                          columns: [
                              { image: logoBase64, width: 120, height: 40 },
                              {
                                  text: "Cash Call Request - " + row.cash_call_reference,
                                  alignment: "right",
                                  bold: true,
                                  fontSize: 13,
                                  margin: [0, 20, 15, 0]
                              }
                          ]
                      },

                      line,

                      twoCol(
                        { text: "Managing Agent", style: "label" },
                        { text: row.Lead_Underwriter, style: "value" },
                        { text: "DCA", style: "label" },
                        { text: row.TPA, style: "value" }
                      ),

                      twoCol(
                        { text: "Broker", style: "label" },
                        { text: row.Broker, style: "value" },
                        { text: "Coverholder", style: "label" },
                        { text: row.Coverholder, style: "value" }
                      ),

                      {
                        columns: [
                          { width: "50%", columns: [labelValue({ text: "Insured Name", style: "label" }, { text: row.Insured_Name, style: "value" })] },
                          { width: "50%", text: "" }
                        ]
                      },                 

                      { text: " ", margin: [0, 2] },

                      /* TOP INFO (2 COLUMNS) */

                      twoCol({ text: "Recipient Name", style: "label" }, { text: formatRecipientsName(safeRecipients), style: "value" }, { text: "Requestor Name", style: "label" }, { text: formatRecipientsName(resolveCreatedByEmail(row)), style: "value" }),
                      twoCol({ text: "Recipient Company", style: "label" }, { text: row.Broker, style: "value" }, { text: "Requestor Company", style: "label" }, { text: row.TPA, style: "value" }),
                      twoCol({ text: "Recipient Email", style: "label" }, { text: formatRecipients(safeRecipients), style: "value" }, { text: "Requestor Email", style: "label" }, { text: resolveCreatedByEmail(row), style: "value" }),

                      

                      { text: " ", margin: [0, 2] },

                      /* CASH CALL AMOUNT */
                      {
                          table: {
                              widths: ["*", "auto"],
                              body: [
                                  [
                                      {
                                          text: "CASH CALL AMOUNT",
                                          style: "amountBarLabel",
                                          fillColor: "#FFF34D",
                                          margin: [5, 3]
                                      },
                                      {
                                          text: formatMoney(row.Cash_Call_Request_Amount),
                                          style: "amountBarValue",
                                          fillColor: "#FFF34D",
                                          alignment: "right",
                                          margin: [5, 3]
                                      }
                                  ]
                              ]
                          }
                      },

                      { text: " ", margin: [0, 2] },

                      /* CASH CALL COMMENTS */
                     // CASH CALL COMMENTS (left & right) – SAME PATTERN AS BDX / CLAIM
                      {
                        columns: [
                          // LEFT BOX
                          {
                            width: "50%",
                            table: {
                              headerRows: 1,
                              widths: ["40%", "60%"],
                              body: [
                                [
                                  {
                                    text: "Cash Call Comments",
                                    style: "sectionHeader",
                                    colSpan: 2,
                                    fillColor: "#faedbe",
                                    alignment: "left"
                                  },
                                  ""
                                ],
                                [
                                  { text: "Established Loss Fund:", style: "label" },
                                  { text: isYes(row.Loss_Fund_Exists) ? "Yes" : "No", style: "value" }
                                ],
                                [
                                  { text: "Cash Call Reason:", style: "label" },
                                  { text: row.Cash_Call_Reason || "—", style: "value" }
                                ],
                                ...(row.reason_manual
                                  ? [
                                      [
                                        { text: "", style: "label" },
                                        {
                                          text: row.reason_manual,
                                          style: "value",
                                          margin: [0, 0, 0, 2]
                                        }
                                      ]
                                    ]
                                  : []),
                                [
                                  { text: "Above Authority Payment?:", style: "label" },
                                  { text: isYes(row.Authority_Scope) ? "Yes" : "No", style: "value" }
                                ]
                              ]
                            },
                            layout: {
                              hLineWidth: () => 1,
                              vLineWidth: () => 1,
                              hLineColor: () => "#000",
                              vLineColor: () => "#000"
                            }
                          },

                          // RIGHT BOX
                          {
                            width: "50%",
                            table: {
                              headerRows: 1,
                              widths: ["*"],
                              body: [
                                [
                                  {
                                    text: "Cash Call Comments",
                                    style: "sectionHeader",
                                    fillColor: "#faedbe",
                                    alignment: "left"
                                  }
                                ],
                                [
                                  {
                                    text: padComments(row.cash_call_comments),
                                    style: "value",
                                    margin: [5, 5, 5, 5]
                                  }
                                ]
                              ]
                            },
                            layout: {
                              hLineWidth: () => 1,
                              vLineWidth: () => 1,
                              hLineColor: () => "#000",
                              vLineColor: () => "#000"
                            }
                          }
                        ],
                        columnGap: 20,
                        margin: [0, 2, 0, 2]
                      },

                      { text: " ", margin: [0, 2] },

                      /* BDX + CLAIM DETAIL */
                      // --- BDX DETAILS + CLAIM DETAILS SECTION ---
                      {
                        columns: [
                          {
                            width: "50%",
                            table: {
                              headerRows: 1,
                              widths: ["35%", "65%"],
                              body: [
                                [
                                  { text: "BDX Details", style: "sectionHeader", colSpan: 2, fillColor: "#faedbe", alignment: "left" },
                                  ""
                                ],
                                [{ text: "UMR", style: "label" }, { text: row.UMR || "—", style: "value" }],
                                [{ text: "Risk Code", style: "label" }, { text: row.Risk_Code || "—", style: "value" }],
                                [{ text: "CAT Code", style: "label" }, { text: row.Cat_Code || "—", style: "value" }],
                                [{ text: "Section #", style: "label" }, { text: row.Section_No || "—", style: "value" }],
                                [{ text: "Currency / YOA", style: "label" }, { text: `${row.Currency_Type || "—"} / ${row.Year_of_Account || "—"}`, style: "value" }],
                                //[{ text: "YOA", style: "label" }, { text: row.Year_of_Account || "—", style: "value" }],
                                [{ text: "LimeIQ Key", style: "label" }, { text: row.Bdx_Key || "—", style: "value" }]
                               
                              ],
                              layout: tightBoxLayout
                            }
                          },

                          {
                            width: "50%",
                            table: {
                              headerRows: 1,
                              widths: ["35%", "65%"],
                              body: [
                                [
                                  { text: "Claim Details", style: "sectionHeader",  colSpan: 2, fillColor: "#faedbe", alignment: "left" },
                                  ""
                                ],
                                [{ text: "DCA Claim Reference", style: "label" }, { text: row.Claim_Reference || "—", style: "value" }],
                                [{ text: "Sanctions Check", style: "label" }, { text: row.Sanctions_Check || "—", style: "value" }],
                                [{ text: "Loss Country", style: "label" }, { text: row.Claim_Country || "—", style: "value" }],
                                // Only Canadian claims carry a province (LIQ-1190).
                                ...(row.Claim_Country === "Canada"
                                  ? [[{ text: "Loss Province", style: "label" }, { text: row.Claim_Province || "—", style: "value" }]]
                                  : []),
                                // Loss / Policy State are US-only, matching the form (shown
                                // only for USA). States auto-filled from the BDX stay on the
                                // record when the country is changed, so don't print them for
                                // another country. No country (older records) keeps both rows.
                                ...(!row.Claim_Country || row.Claim_Country === "USA"
                                  ? [
                                      [{ text: "Loss State", style: "label" }, { text: row.Claim_State || "—", style: "value" }],
                                      [{ text: "Policy State", style: "label" }, { text: row.SL_Tax_State || "—", style: "value" }]
                                    ]
                                  : []),
                                [{ text: "Loss Description", style: "label" }, { text: {
                                    text: padComments(row.Cause_of_Loss),
                                    style: "value",
                                    margin: [5, 5, 5, 5]
                                  } || "—", style: "value" }]
                              ],
                              layout: tightBoxLayout
                            }
                          }
                        ],
                        columnGap: 20,   // <-- FIX #1: ensures spacing between columns
                        margin: [0, 2]  // <-- FIX #2: stops touching sections above/below
                      },


                      { text: " ", margin: [0, 2] },

                      /* PROGRAM + ACCOUNT */
                      {
                          columns: [
                              {
                                  width: "50%",
                                  table: {
                                      widths: ["*", "*"],
                                      body: [                                      
                                          [{ text: "Program Details", style: "sectionHeader", colSpan: 2, fillColor: "#faedbe", alignment: "left"}, ""],
                                          [{ text: "Binder Inception / Expiration", style: "label" }, { text: `${row.Binder_Contract_Inception || "—"} / ${row.Binder_Contract_Expiry || "—"}`, style: "value" } ],                                      
                                      [{ text: "Policy Inception / Expiration", style: "label" }, { text: `${row.Risk_Effective_Date || "—"} / ${row.Risk_Expiration_Date || "—"}`, style: "value" } ],                                      
                                      [{ text: "Date of Loss", style: "label" }, { text: row.Date_of_Loss || "—", style: "value" }],
                                      [{ text: "Date Claim Made", style: "label" }, { text: row.Claim_Date || "—", style: "value" }]
                                      ],
                                      layout: tightBoxLayout
                                  }
                              },
                              {
                                  width: "50%",
                                  table: {
                                      widths: ["*", "*"],
                                      body: [
                                          [{ text: "Account Details", style: "sectionHeader",  colSpan: 2, fillColor: "#faedbe", alignment: "left" }, ""],
                                          [{ text: "Account Name", style: "label" }, { text: row.account_name || "—", style: "value" }],
                                          [{ text: "Account ID", style: "label" }, { text: row.account_id || "—", style: "value" }],
                                          [{ text: "Initial Loss Fund Value", style: "label" }, { text: formatMoney(row.Initial_Loss_Fund_Value || 0.0), style: "value" }],
                                          [{ text: "Current Loss Fund Balance", style: "label" }, { text: formatMoney(row.Current_Loss_Fund_Balance || 0.0), style: "value" }]                                          
                                      ],
                                      layout: tightBoxLayout
                                  }
                              }
                          ],
                          columnGap: 20
                      },

                     // { text: "", pageBreak: "before" },
                     { text: " ", margin: [0, 2] },

                      /* CASH CALL DETAIL */
                      {
                      columns: [
                          {
                              width: "50%",
                              table: {
                                  widths: ["*", "auto"],
                                  body: [
                                      [{ text: "Cash Call Details", style: "sectionHeader",  colSpan: 2, fillColor: "#faedbe", alignment: "left" }, ""],
                                      [{ text: "Indemnity", style: "label" }, { text: formatMoney(row.Total_Indemnity_Amount), style: "value" }], 
                                      [{ text: "TPA Fees", style: "label" }, { text: formatMoney(row.TPA_Fees_Amount), style: "value" }],
                                      [{ text: "Adjusters Fees", style: "label" }, { text: formatMoney(row.Adjusters_Fees_Amount), style: "value" }],
                                      [{ text: "Defence Fees", style: "label" }, { text: formatMoney(row.Defence_Fees_Amount), style: "value" }],
                                      [{ text: "Expenses", style: "label" }, { text: formatMoney(row.Expenses_Amount), style: "value" }],
                                      [{ text: "Attorney Coverage", style: "label" }, { text: formatMoney(row.Attorney_Coverage_Amount), style: "value" }],
                                      [{ text: "Total Indemnity Amount", style: "label" }, { text: formatMoney(row.Total_Indemnity_Amount), style: "value" }],
                                      [{ text: "Total Fees Amount", style: "label" }, { text: formatMoney(row.Total_Fee_Amount), style: "value" }],
                                      [ { text: "Cash Call Request Amount", bold: true, fillColor: "#FFF34D" },
                                      { text: formatMoney(row.Cash_Call_Request_Amount), bold: true, fillColor: "#FFF34D" }]
                                  ]
                              }
                          },
                          {
                              width: "50%",
                              table: {
                                  widths: ["*", "auto"],
                                  body: [
                                      [{ text: "Documents / Supporting Information", style: "sectionHeader",  colSpan: 2, fillColor: "#faedbe", alignment: "left" }, ""],
                                      // Rows are driven by the SAME constant the
                                      // Documentation step uploads against (plus
                                      // "Other"). They were previously hard-coded
                                      // to different labels ("Policy", "IA
                                      // Report", ...) which never matched a
                                      // stored document_type, so every row always
                                      // read "Not Attached".
                                      ...[...CASH_CALL_SUPPORTING_DOCUMENT_OPTIONS.map((o) => o.value), 'Other'].map(
                                        (t) => ([{ text: t, style: "label" }, { text: docStatus(t), style: "value" }])
                                      )
                                  ]
                              }
                          }
                      ],
                      columnGap: 20
                      },

                  ]
              };
              return docDefinition;
        }

        const handleExport = async () => {
          try {
            isExporting.value = true;
            isExported.value = false;

            // 1️⃣ Pull full dataset (NOT paginated)
            const fullContext = {
              filter: "",        // no search
              filters: [],        // no column filters (table handles via mainFilters)
              currentPage: 1,
              perPage: 5000,       // load max rows
              sortBy: null,
              sortDesc: false
            };

            const fullItems = await itemsProvider(fullContext);

            // Keys toggled "funding not required" are captured in data but
            // excluded from exports (LIQ-1992 AC). `funding_required` is
            // flattened from the cash call's primary key, so this is exact
            // for single-key cash calls; for a multi-key Co-Lead cash call it
            // only reflects the primary key's toggle - see `item.keys` for
            // every key's own share_percentage/funding_required.
            const exportableItems = (fullItems || []).filter(item => item.funding_required !== false);

            if (!exportableItems || !exportableItems.length) {
              // showToast, not this.$bvToast: `this` is undefined inside
              // setup(), so the old call threw and no message ever showed.
              showToast("No data available to export");
              return;
            }

            // 2️⃣ Prepare header row
            const headers = CashCallManagementTableColumns.map(col => col.label);

            // 3️⃣ Prepare export rows
            const exportData = exportableItems.map(item => {
              const row = {};

              CashCallManagementTableColumns.forEach(col => {
                const key = col.key;

                // Format currency fields using your formatter
                if ([
                  "TPA_Fees_Amount",
                  "Adjusters_Fees_Amount",
                  "Defence_Fees_Amount",
                  "Expenses_Amount",
                  "Attorney_Coverage_Amount",
                  "Total_Fee_Amount",
                  "Total_Indemnity_Amount",
                  "Cash_Call_Request_Amount"
                ].includes(key)) {
                  row[col.label] = formatCurrency(item[key]);
                }

                // Date fields
                else if (key === "created_on") {
                  row[col.label] = item.created_on
                  ? new Date(item.created_on).toLocaleDateString("en-US")
                  : "";
                }

                // Default mapping
                else {
                  row[col.label] = item[key] || "";
                }
              });

              return row;
            });

            // 4️⃣ Build XLSX
            const worksheet = XLSX.utils.json_to_sheet(exportData);

            // Add header row explicitly
            XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: "A1" });

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "CashCall List");

            // 5️⃣ Save the file
            XLSX.writeFile(workbook, "CashCall_List.xlsx");

            isExported.value = true;
          } catch (err) {
            console.error("Export failed:", err);
            showToast("Export failed. Check console for details.", 'error');
          } finally {
            isExporting.value = false;
          }
        };

        const formatMoneyInput = (value) => {
          if (!value) return ''

          let cleaned = String(value).replace(/[^0-9.]/g, '')

          const firstDot = cleaned.indexOf('.')
          if (firstDot !== -1) {
            cleaned =
              cleaned.slice(0, firstDot + 1) +
              cleaned.slice(firstDot + 1).replace(/\./g, '')
          }

          let intPart = cleaned
          let decPart = ''

          if (cleaned.includes('.')) {
            ;[intPart, decPart] = cleaned.split('.')
          }

          const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

          if (decPart !== undefined && decPart !== '') {
            return `${formattedInt}.${decPart.slice(0, 2)}`
          }

          return formattedInt
        }

        const handleMoneyInput = (key, value) => {
          const formatted = formatMoneyInput(value)

                    if (key === 'paidIndemnity') {
                      paidIndemnity.value = formatted
                    } else {
                      feeBreakdown.value = {
            ...feeBreakdown.value,
            [key]: formatted
          }
          }
        }

        const handleIndemnityInput = (value) => {
          const cleaned = String(value || '').replace(/[^\d.]/g, '')

          const parts = cleaned.split('.')
          const normalized =
            parts.length > 1
              ? `${parts[0]}.${parts.slice(1).join('')}`
              : parts[0]

          const [intPart, decPart] = normalized.split('.')

          const formattedInt = intPart
            ? Number(intPart).toLocaleString('en-US')
            : ''

          paidIndemnity.value =
            decPart !== undefined ? `${formattedInt}.${decPart}` : formattedInt
        }

        const handleMoneyInput1 = (key, value) => {
          const cleaned = String(value || '').replace(/[^\d.]/g, '')

          const parts = cleaned.split('.')
          const normalized =
            parts.length > 1
              ? `${parts[0]}.${parts.slice(1).join('')}`
              : parts[0]

          const [intPart, decPart] = normalized.split('.')
          const formattedInt = intPart ? Number(intPart).toLocaleString('en-US') : ''
          const formattedValue =
            decPart !== undefined ? `${formattedInt}.${decPart}` : formattedInt

          feeBreakdown.value = {
            ...feeBreakdown.value,
            [key]: formattedValue
          }
        }

        // Map a saved DB key (PascalCase serializer shape) into the internal row
        // shape the co-lead PDF builder expects (umr, underwriter_lead, broker[],
        // share_percentage, loss fund, ...). Mirrors what the wizard sends the
        // email PDF so the downloaded PDF matches the emailed one exactly.
        const coleadKeyForPdf = (k) => ({
          umr: k.UMR || k.Bdx_Key || '',
          bdx_key: k.Bdx_Key ? [k.Bdx_Key] : [],
          rc: k.Risk_Code || '',
          sn: k.Section_No || '',
          cc: k.Cat_Code || '',
          currency_type: k.Currency_Type || '',
          // Was missing -> "Currency / YOA" showed "USD / —".
          year_of_account: k.Year_of_Account || '',
          claimref: k.Claim_Reference ? [k.Claim_Reference] : [],
          underwriter_lead: k.Lead_Underwriter || '',
          broker: k.Broker ? String(k.Broker).split(',').map((b) => b.trim()).filter(Boolean) : [],
          tpa: k.TPA || '',
          coverholder: k.Coverholder || '',
          insured_name: k.Insured_Name || '',
          date_of_loss: k.Date_of_Loss || '',
          date_claim_made: k.Claim_Date || '',
          // Were missing -> Claim Details (Loss State / Policy State / Loss
          // Description) came out blank in the downloaded PDF.
          loss_country: k.Claim_Country || '',
          loss_state: k.Claim_State || '',
          loss_sl_state: k.SL_Tax_State || '',
          canada_province: k.Claim_Province || '',
          naic_code: k.NAIC_Code || '',
          cause_of_loss: k.Cause_of_Loss || '',
          share_percentage: k.share_percentage,
          funding_required: k.funding_required !== false,
          initial_loss_fund_value: k.Initial_Loss_Fund_Value,
          current_loss_fund_balance: k.Current_Loss_Fund_Balance
        })

        const isColeadRow = (row) => !!(row && (row.is_co_lead || (row.keys && row.keys.length > 1)))

        // Arguments for the co-lead (multi-UMR) PDF, built from a list row. Used
        // by both the Download button and the email icon so the downloaded,
        // emailed and packaged PDFs are the same.
        const coleadPdfArgs = (row, recipients) => {
            const keys = (row.keys || []).map(coleadKeyForPdf)
            const num = (v) => {
              if (v === null || v === undefined || v === '') return 0
              const n = Number(String(v).replace(/[^0-9.-]/g, ''))
              return isNaN(n) ? 0 : n
            }
            return {
              cashCall: {
                reference: row.cash_call_reference || row.reference || '',
                amount: num(row.Cash_Call_Request_Amount),
                comments: row.cash_call_comments || '',
                claim_reason: row.Cash_Call_Reason || '',
                // The PDF reads claim_loss_established / claim_authority_scope
                // (not loss_fund_exists) - wrong keys left these blank.
                claim_loss_established: !!row.Loss_Fund_Exists,
                claim_authority_scope: !!row.Authority_Scope,
                sanctions_check: row.Sanctions_Check || '',
                indemnity: num(row.Total_Indemnity_Amount),
                tpa_fees: num(row.TPA_Fees_Amount),
                adjusters_fees: num(row.Adjusters_Fees_Amount),
                defence_fees: num(row.Defence_Fees_Amount),
                expenses: num(row.Expenses_Amount),
                attorney_coverage: num(row.Attorney_Coverage_Amount),
                total_indemnity: num(row.Total_Indemnity_Amount),
                total_fee: num(row.Total_Fee_Amount),
                supporting_documents: row.supporting_documents || [],
                // Include "Other" so that row shows, matching the wizard/email
                // list (documentationOptions = constant + "Other").
                document_types: [...CASH_CALL_SUPPORTING_DOCUMENT_OPTIONS.map((o) => o.value), 'Other']
              },
              keys,
              recipients,
              // Created_By may be a name or an email. Keep a real name in the name
              // slot and an email in the email slot (never an email in name).
              requestor: (() => {
                const cb = row.Created_By || row.created_by || ''
                const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cb)
                return isEmail ? { name: '', email: cb } : { name: cb, email: '' }
              })()
            }
        }

        const downloadCashCallPdf = (row) => {
          // Co-lead: build with the multi-UMR layout (same as the email PDF).
          if (isColeadRow(row)) {
            const recipients = (row.recipient_emails || '').split(',').map((e) => e.trim()).filter(Boolean)
            const docDefinition = buildColeadDocDefinition(coleadPdfArgs(row, recipients))
            pdfMake.createPdf(docDefinition).download(`CashCall-${row.cash_call_reference}.pdf`)
            return
          }
          // Pass the saved recipients so the DOWNLOADED pdf shows the same
          // Recipient Name / Email as the one that was emailed. Without the
          // second arg those rows rendered blank (the email path passes
          // `person`, this one didn't).
          const docDefinition = generateDocDefinition(row, row.recipient_emails);
          pdfMake.createPdf(docDefinition).download(`CashCall-${row.cash_call_reference}.pdf`);
        };

        const enableColumnResize = () => {
          const table = document.querySelector(".resizable-table table");
          if (!table) return;

          const ths = table.querySelectorAll("th");

          ths.forEach((th) => {
            if (th.querySelector(".resize-handle")) return;

            const handle = document.createElement("div");
            handle.className = "resize-handle";

            th.style.position = "relative";
            th.appendChild(handle);

            let startX, startWidth;

            handle.addEventListener("mousedown", (e) => {
              e.stopPropagation();
              e.preventDefault();

              startX = e.pageX;
              startWidth = th.offsetWidth;
              let didDrag = false;

              const onMouseMove = (e) => {
                didDrag = true;
                const newWidth = startWidth + (e.pageX - startX);
                th.style.width = `${newWidth}px`;
                th.style.minWidth = `${newWidth}px`;
                th.style.maxWidth = `${newWidth}px`;

                const index = Array.from(th.parentNode.children).indexOf(th);
                const col = table.querySelectorAll("colgroup col")[index];
                if (col) {
                  col.style.width = `${newWidth}px`;
                  col.style.minWidth = `${newWidth}px`;
                }

                // Sync sticky column body cells if this is the sticky column
                if (th.classList.contains("b-table-sticky-column")) {
                  const stickyTds = table.querySelectorAll("td.b-table-sticky-column");
                  stickyTds.forEach((td) => {
                    td.style.width = `${newWidth}px`;
                    td.style.minWidth = `${newWidth}px`;
                    td.style.maxWidth = `${newWidth}px`;
                  });
                }
              };

              const onMouseUp = () => {
                document.removeEventListener("mousemove", onMouseMove);
                document.removeEventListener("mouseup", onMouseUp);

                // If the mouse actually moved (a real drag), suppress the
                // click event that the browser fires after mouseup so
                // Bootstrap Vue's sort handler does not trigger.
                if (didDrag) {
                  th.addEventListener("click", (clickEvt) => {
                    clickEvt.stopPropagation();
                    clickEvt.preventDefault();
                  }, { capture: true, once: true });
                }
              };

              document.addEventListener("mousemove", onMouseMove);
              document.addEventListener("mouseup", onMouseUp);
            }, true);
          });
        };

        watch([mainFilters, bdxFilters], () => {
          selectedSubmittedId.value = null
          handleRefresh()
        }, { deep: true })

        watch(selectedSubmittedId, () => {
          handleRefresh()
        })

        onMounted(async () => {
          enableColumnResize()
          Vue.prototype.$emitter.on('files-uploaded', ({ type, success }) => {
              if (!success || 'bdx' !== type) return

              handleRefresh()
          })
          Vue.prototype.$emitter.on('cash-call-form:success', async (payload) => {
            if (!payload.status) return
            const cashCallObj = payload.obj.data
            if (!cashCallObj.id) {
              console.warn('Invalid cashcall object', cashCallObj)
              return
            }
            // The create response has no supporting_documents and a brand-new
            // cash call isn't in the list yet, so carry the attached doc types
            // straight from the form - otherwise the emailed PDF shows every
            // document as "Not Attached".
            selectedCashCall.value = {
              ...cashCallObj,
              supporting_documents:
                (cashCallObj.supporting_documents && cashCallObj.supporting_documents.length)
                  ? cashCallObj.supporting_documents
                  : (payload.supporting_documents || [])
            }
          })
          Vue.prototype.$emitter.on("cashcall:email-sent", async () => {
            await handleRefresh();
          });
          Vue.prototype.$emitter.on("refresh-cashcall-grid", async () => {
            await handleRefresh();
          });
          Vue.prototype.$emitter.on('cashcall:open-email-modal', ({ cashCall }) => {
            if (!cashCall) return;

            nextTick(() => {
              showMailModal.value = true;
            });
          });
          Vue.prototype.$emitter.on('cashcall:open-by-id', async function(payload) {
            if (!payload) return

            const cashCallId = payload.cashCallId
            const bdxKey = payload.bdxKey
            const action = payload.action

            if (!cashCallId) return

            if (bdxKey) {
              directLinkBdxKey.value = bdxKey
            }

            directLinkAction.value = action
            directLinkUrgent.value = !!payload.urgent
            directLinkHighImportance.value = !!payload.highImportance
            directLinkCashCallId.value = cashCallId
            await handleRefresh()
          })


        })
        return {
      CashCallManagementTableColumns,
      commentTooLong,
      commentCounterText,
      coleadComment,
      CashCallActionTableColumns,
      uniqueId,
      format,
      coLeadTooltip,

      userCompanyRoleName,
      userCompanyRoleValue,

      modelValue,

      mainFilters,

      dataTableRef,
      submittedTableRef,
      handleRefresh,

      total,
      submittedTotal,

      itemsProvider,
      submittedItemsProvider,

      directLinkAction,
      directLinkCashCallId,
      directLinkBdxKey,
      confirmWithdrawn,

      tableTbodyTrClass,
      handleEdit,
      handleDelete,
      toggleSubmitted,
      formatCurrency,
      splitList,
      listColumns,
      cellList,
      approverLabel,
      approverTooltip,
      bottomHoverTextColumns,
      formatRecipientsName,
      buildMailtoLink,
      disabledPaidDates,
      loading,
      actionTableLoading,
      userId,      
      cashCallRecordById,
      selectedSubmittedId,

      downloadCashCallPdf,
      generateDocDefinition,
      isEmailDisabled,
      emailIconTooltip,
      isEditDisabled,
      isReadOnlyRole,
      emailIconStyle,
      editIconStyle,
      generatePdfBlob,
      handleMailSubmit,
      emailCashCall,
      isDeleteDisabled,
      deleteIconStyle,
      handleExport,
      canChangeStatus,
      confirmStatusChange,
      isExporting,
      isExported,
      showMailModal,
      selectedCashCall,
      showStatusModal,
      statusMode,
      selectedStatusItem,
      // Co-Lead per-key partial payments
      isColeadPaid,
      isColeadReject,
      coleadPayKeys,
      pagedColeadPayKeys,
      coleadPayInputs,
      coleadPayErrors,
      coleadPaySavingKeyId,
      coleadRejectingKeyId,
      coleadKeyExpanded,
      toggleColeadKey,
      onColeadKeyPaymentTypeChange,
      rejectColeadKey,
      standardAlreadyPaid,
      standardRemaining,
      standardIndemnityRemaining,
      standardPayments,
      coleadRemaining,
      coleadNeedsBreakdown,
      coleadHasPaymentType,
      coleadFeeFields,
      coleadPaidIndemnity,
      coleadPaidFeesTotal,
      coleadKeyHeader,
      setColeadPayField,
      addColeadKeyPayment,
      paymentType,
      paidIndemnity,
      paidDate,
      receivedDate,
      statusComment,
      feeBreakdown,
      statusErrors,
      statusObserver,
      activeFeeFields,
      totalPaidFee,
      submitStatusChange,
      DATE_FORMAT,
      formatMoneyInput,
      handleMoneyInput,
      handleIndemnityInput
    }
    }
})

</script>

<style scoped>
.co-lead-badge {
  display: inline-block;
  margin-left: 6px;
  margin-top: 2px;
  padding: 1px 6px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  background-color: #1c4a50;
  color: #fff;
  white-space: nowrap;
  cursor: default;
  flex-shrink: 0;
}

/* Long emails end in "…" (full address on hover) instead of spilling into
   the next column. */
.cash-call-mailto {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
  text-decoration: underline;
  cursor: pointer;
}

/* Hover text (Comments, Insured Name) cut off at the cell edge itself: an
   inline span keeps the full text's width past the cell, so its tooltip
   centred over the next columns instead of over the cell. */
.cell-hover-text {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Link + "(+N)": the link shrinks first so the (+N) always stays visible. */
.cell-with-more {
  display: flex;
  align-items: center;
  min-width: 0;
}
.cell-with-more > :first-child {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* "(+N)" after the first Recipient / TPA Approver; hover lists them all. */
.more-badge {
  flex-shrink: 0;
  margin-left: 4px;
  font-weight: 600;
  color: #1c4a50;
  border-bottom: 1px dotted #1c4a50;
  cursor: default;
}

>>> .filters {
  column-gap: 10px;
  font-size: 12px;
}

>>> .filters + div {
  margin-top: auto;
}

>>> .filters + div .base-data-table-search {
  max-width: 320px;
}

>>> .b-table-sticky-column > div {
  min-width: 1px;
  margin: 0 !important;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end !important;
}

>>> th.b-table-sticky-column,
>>> td.b-table-sticky-column > div {
  padding-right: 10px !important;
  padding-left: 10px !important;
}

>>> td.b-table-sticky-column > div .custom-checkbox {
  margin-left: 10px;
  display:none;
}
/* The select-all checkbox in the actions column header showed up as a stray
   box left of STATUS; row selection isn't used here (LIQ-1973). */
>>> th.b-table-sticky-column .custom-checkbox {
  display: none;
}
>>> .table.b-table {
  table-layout: fixed;
}
>>> .table.b-table colgroup > col:first-child {
  width: 128px !important;
}
>>> .table.b-table tbody > tr > td > div {
  white-space: normal;
}
>>> .toptable .table.b-table colgroup > col:first-child {
  width: 35px !important;
}

 >>> .toptable .input-group {
  width: 300px !important;
}

/* 225 */

>>> .toptable .table.b-table td {
  padding-top: 1px !important;
  padding-bottom: 1px !important;
  font-size: 12px !important;
}

>>> .toptable .table.b-table th {
  padding-top: 6px !important;
  padding-bottom: 6px !important;
  font-size: 12px !important;
  font-weight: bold !important;
  background-color: aliceblue !important;
}

>>> .toptable .p-3 {
 padding: 0.5rem !important;
}

>>> .toptable .card-body {
 padding: 0.15rem !important;
}

>>> .toptable .card .card-footer {
 padding: 0.5rem !important;
}

>>> .bottomtable .p-3 {
 padding: 0.5rem !important;
}

>>> .bottomtable .card-body {
 padding: 0.15rem !important;
}

>>> .bottomtable .table.b-table td {
  padding-top: 1px !important;
  padding-bottom: 1px !important;
  font-size: 12px !important;
}

/* One line per row in both tables: long values are cut with an ellipsis (full
   text is in the cell's hover tooltip) instead of wrapping and growing the row. */
>>> .toptable .table.b-table tbody > tr > td > div,
>>> .bottomtable .table.b-table tbody > tr > td > div {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

>>> .bottomtable .table.b-table th {
  padding-top: 6px !important;
  padding-bottom: 6px !important;
  font-size: 12px !important;
  font-weight: bold !important;
  background-color: aliceblue !important;
}

>>> .bottomtable .card .card-footer {
 padding: 0.5rem !important;
}

>>> .swal2-validation-message {
  padding: 0px !important;
  background-color: red !important;
}

.cashcall-paid-modal {
  max-height: 90vh !important;
    display: flex !important;
    flex-direction: column !important;
    padding-bottom: 0.8rem !important;
}

.cashcall-paid-modal .modal-dialog {
  max-width: 680px;
}

.limeiq-summary {
  background: #eef6f1;
  border-radius: 6px;
  padding: 12px 14px;
  margin-bottom: 6px;
}

.limeiq-money {
  color: #2e7d32;
  font-weight: 600;
}

.limeiq-breakdown {
  margin-top: 6px;
  display: flex;
  gap: 20px;
  font-size: 13px;
  color: #2e7d32;
}

.limeiq-field {
  margin-bottom: 14px;
}

.limeiq-field label {
  display: block;
  font-weight: normal;
  font-size: 16px;
  font-family: 'Segoe UI Regular';
  margin-bottom: 6px;
  color: #333;
}

.limeiq-select {
  width: 150px;
}

.limeiq-input,
.limeiq-textarea {
  width: 150px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 8px 10px;
  font-size: 14px;
  background: #fff;
}

.limeiq-textarea {
  width: 220px !important;
}

.limeiq-input {
  height: 38px;
}

.limeiq-textarea {
  min-height: 65px;
  resize: vertical;
}

/* "245 / 600" under each status / rejection / payment comment (LIQ-1850). */
.comment-counter {
  display: block;
  margin-top: 2px;
  font-size: 11px;
  color: #8898aa;
}

.comment-counter--over {
  color: #dc3545;
  font-weight: 600;
}

.limeiq-fee-scroll {
  max-height: 220px;
  overflow-y: auto;
  padding-right: 4px;
}

.limeiq-fee-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
}

.limeiq-fee-item {
  display: flex;
  flex-direction: column;
}

.limeiq-sub-label {
  font-size: 13px;
  font-weight: 500;
  color: #555;
}

.limeiq-input-full {
  width: 150px;
}

.limeiq-fee-total {
  margin-top: 12px;
  font-weight: 600;
  font-size: 13px;
  color: #2e7d32;
  text-align: right;
}

.limeiq-payment-total {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  font-weight: 600;
  color: #2e7d32; /* same green tone you already use */
  margin-top: 10px;
  font-family: 'Segoe UI Regular';
}

/* ---- Co-Lead per-key partial payments (Mark as Paid) ---- */
.colead-pay__hint {
  font-size: 12px;
  color: #6c757d;
  margin: 6px 0 12px;
}
.colead-pay__key {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 10px;
}
.colead-pay__key--done {
  border-color: #2e7d32;
  background: #f4faf5;
}
.colead-pay__key-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}
/* Clickable accordion header for a co-lead key. */
.colead-pay__key-head--toggle {
  cursor: pointer;
  margin-bottom: 0;
  gap: 8px;
}
.colead-pay__key-chevron {
  color: #8898aa;
  flex-shrink: 0;
}
.colead-pay__key-title {
  font-weight: 700;
  color: #1c4a50;
  font-size: 13px;
  /* The head is justify-content:space-between. In Mark as Paid the trailing
     "Remaining" span (margin-left:auto) soaks up the slack so the title sits
     left; the Reject modal has no such span, which let space-between push the
     title to the far right. Owning the free space here keeps the title beside
     the chevron in BOTH modals. */
  margin-right: auto;
  text-align: left;
}
/* Remaining summary shown in the collapsed header (pushed to the right, before
   the status badge). */
.colead-pay__key-remaining {
  margin-left: auto;
  font-size: 12px;
  color: #525f7f;
}
.colead-pay__key-body {
  margin-top: 8px;
}
.colead-pay__key-badge {
  font-size: 11px;
  font-weight: 700;
  color: #2e7d32;
}
.colead-pay__key-badge--pending {
  color: #8898aa;
}
.colead-pay__key-badge--rejected {
  color: #f5365c;
}
/* Finalized short settlement (partial payment marked final) - amber to signal
   the UMR is closed but underpaid, distinct from the green "Fully paid". */
.colead-pay__key-badge--partial {
  color: #d97706;
}
/* Co-lead reject: intro line above the per-key list. */
.colead-reject__intro {
  font-size: 13px;
  color: #525f7f;
  margin-bottom: 12px;
}
/* Per-key Reject action */
.colead-pay__reject-row {
  margin-top: 8px;
}
.colead-pay__reject-btn {
  border: 1px solid #f5365c;
  background: #fff;
  color: #f5365c;
  font-weight: 600;
  font-size: 12px;
  padding: 5px 12px;
  border-radius: 6px;
  cursor: pointer;
}
.colead-pay__reject-btn:hover:not(:disabled) {
  background: #f5365c;
  color: #fff;
}
.colead-pay__reject-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.colead-pay__key-figures {
  display: flex;
  gap: 14px;
  font-size: 12px;
  color: #32325d;
  margin-bottom: 6px;
}
.colead-pay__unfunded {
  font-size: 12px;
  color: #8898aa;
  font-style: italic;
}
.colead-pay__history {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  margin-bottom: 6px;
}
.colead-pay__history td {
  padding: 3px 6px;
  border-bottom: 1px solid #eef0f3;
}
.colead-pay__hist-amt {
  text-align: right;
  font-weight: 600;
  color: #1c4a50;
  white-space: nowrap;
}
.colead-pay__hist-cmt {
  color: #8898aa;
}
.colead-pay__no-history {
  font-size: 12px;
  color: #adb5bd;
  margin: 0 0 6px;
}
.colead-pay__add {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
}
.colead-pay__add-row {
  display: flex;
  gap: 8px;
  align-items: center;
}
.colead-pay__add-actions {
  display: flex;
  justify-content: flex-end;
}
.colead-pay__comment {
  width: 100%;
  min-height: 48px;
  resize: vertical;
}
.colead-pay__amt-wrap {
  position: relative;
  display: inline-block;
}
.colead-pay__amt-prefix {
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  color: #525f7f;
  pointer-events: none;
}
.colead-pay__amt {
  max-width: 120px;
  padding-left: 18px;
}
.colead-pay__date {
  max-width: 150px;
}
/* Installment history on a STANDARD cash call. Reuses the co-lead history table
   styling so both flows read the same. */
.standard-pay__history-wrap {
  margin-bottom: 12px;
}

.standard-pay__history-label {
  display: block;
  font-size: 12px;
  color: #666;
  margin-bottom: 2px;
}

/* Per-key paid breakdown (PARTIAL payments). Mirrors the standard cash call's
   Paid Indemnity + Paid Fees Breakdown block, but sized for the per-UMR
   accordion so several keys can be open without the modal feeling heavy. */
.colead-pay__breakdown {
  margin-top: 8px;
  padding: 8px 10px;
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
}

/* "Paid Indemnity" / "Paid Fees Breakdown" headings - same treatment as
   .limeiq-field label on the standard cash call, a step smaller because these
   sit inside the per-UMR accordion rather than a full-width modal. */
.colead-pay__bd-label {
  display: block;
  font-weight: normal;
  font-size: 14px;
  font-family: "Segoe UI Regular", sans-serif;
  color: #102F3D;
  margin-bottom: 6px;
}

.colead-pay__bd-label:not(:first-child) {
  margin-top: 8px;
}

/* Matches .limeiq-fee-grid on the standard cash call. */
.colead-pay__bd-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
}

/* Matches the EFFECTIVE .limeiq-sub-label on the standard cash call (the later
   definition in this file wins over the earlier one). */
.colead-pay__bd-sub-label {
  display: block;
  font-weight: 400;
  margin-bottom: 6px;
  color: #102F3D;
  font-family: "Segoe UI Regular", sans-serif;
  font-size: 12px;
}

.colead-pay__bd-input {
  width: 100%;
}

/* Matches .limeiq-payment-total on the standard cash call. */
.colead-pay__bd-total {
  margin-top: 12px;
  font-size: 13px;
  font-weight: 600;
  color: #2e7d32;
}

.colead-pay__bd-divider {
  margin: 0 6px;
  color: #ccc;
}

/* Per-key Payment Amount (Full / Partial) selector. */
.colead-pay__paytype {
  margin-bottom: 8px;
}
.colead-pay__paytype-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #525f7f;
  margin-bottom: 3px;
}
.colead-pay__paytype-select {
  max-width: 150px;
}
/* Two payment-date fields (Cash Call Received Date + Paid to Insured Date),
   side by side each with a small label. */
.colead-pay__dates {
  display: flex;
  gap: 16px;
  margin: 8px 0;
  flex-wrap: wrap;
}
.colead-pay__date-field {
  display: flex;
  flex-direction: column;
}
.colead-pay__date-label {
  font-size: 11px;
  font-weight: 600;
  color: #525f7f;
  margin-bottom: 3px;
}
.colead-pay__hist-recv {
  color: #8898aa;
}
.colead-pay__add-btn {
  background: #1c4a50;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.colead-pay__add-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.limeiq-divider {
  color: #999;
}

.limeiq-radio-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: #f54927;
}

>>> .modal-body {
  position: relative;
  padding-top: 0.625rem !important;
  padding-bottom: 2.125rem !important;
  padding-right: 2.125rem !important;
  padding-left: 2.125rem !important;

}

.limeiq-datepicker >>> .mx-input-wrapper {
  width: 150px;
  max-width: 150px;
}

.limeiq-datepicker >>> .mx-input {
  border-radius: 4px;
  border-color: #e9ecef;
  height: 36px;
  width: 150px;
  padding-right: 30px; /* space for calendar icon */
}

.limeiq-datepicker >>> .mx-icon-calendar {
  right: 8px;
}

.limeiq-radio-group {
  display: flex;
  gap: 20px; /* space between radios */
  align-items: center;
}

.limeiq-radio-label {
  display: flex;
  align-items: center;
  gap: 6px; /* space between radio and text */
}

</style>

<style>
/* These MUST be global because BootstrapVue renders <th> outside scoped area */

/* Must be global, NOT scoped */

.swal2-validation-message {
  padding: 0px !important;
  background-color: white !important;
  color:red !important;
}

/* Hover lists (Recipients, TPA Approvers, Comments): one entry per line.
   Global because tooltips render on <body>, outside the scoped area. */
.tooltip.cash-call-list-tooltip .tooltip-inner {
  white-space: pre-line;
  text-align: left;
  max-width: 360px;
}

.resizable-table th {
  position: relative !important;
  padding-right: 40px !important;
  margin-right: 6px;
}

/* Ensure resize handle on sticky column is on top and clickable */
.resizable-table .b-table th.b-table-sticky-column {
  overflow: visible !important;
}

.resizable-table .b-table th.b-table-sticky-column .resize-handle {
  z-index: 1000 !important;
  pointer-events: auto !important;
}

/* Make the body sticky column divider match the header resize-handle exactly:
   same 8px gradient, same right:0 anchor — cells are equal-width via syncStickyColumnWidth
   so both lines land at the identical x-position. */
.resizable-table .b-table td.b-table-sticky-column::after {
  box-shadow: none !important;
  width: 8px !important;
  background: linear-gradient(
    to right,
    transparent 0%,
    rgba(236, 233, 233, 0.605) 50%,
    transparent 100%
  ) !important;
}

/* Push Bootstrap Vue's sort icon (background-image) away from resize handle */
.resizable-table .b-table th[aria-sort]:not(.b-table-sort-icon-left) {
  background-position: right 20px center !important;
}


.resizable-table .resize-handle {
  position: absolute;
  top: 0;
  right: 0;
  width: 8px;
  height: 100%;
  cursor: col-resize;
  z-index: 20;
  

  background: linear-gradient(
    to right,
    transparent 0%,
    rgba(236, 233, 233, 0.605) 50%,
    transparent 100%
  );
}


.btn >>> .base-icon {
width: 14px;
height: 14px;
margin-right: 8px;
}

/* SWEETALERT2 CUSTOM THEME - LIMEIQ STYLE */

.cash-call-management-page .swal2-actions {
  gap: 18px !important;
  margin-top: 20px !important;
}

.cash-call-management-page .swal2-popup {
  border-radius: 12px !important;
  padding: 1.75rem !important;
  box-shadow: 0px 4px 20px rgba(0,0,0,0.15) !important;
}

.cash-call-management-page .swal2-title {
  font-size: 1.45rem !important;
  font-weight: 600 !important;
  color: #1e2a35 !important;
}

.cash-call-management-page .swal2-html-container {
  font-size: 1rem !important;
  color: #4a5568 !important;
  margin-top: 8px !important;
}

.cash-call-management-page .swal2-confirm {
  background-color: #1c4a50 !important;  /* <-- LimeIQ teal/dark green */
  color: white !important;
  border-radius: 8px !important;
  padding: 10px 26px !important;
  font-weight: 400 !important;
  border: none !important;
  box-shadow: none !important;
}

.cash-call-management-page .swal2-cancel {
  background-color: #E2E8F0 !important;
  color: #333 !important;
  border-radius: 8px !important;
  padding: 10px 26px !important;
  font-weight: 500 !important;
}

.cash-call-management-page .swal2-confirm:hover {
   background-color: #0B5345 !important; 
}

.cash-call-management-page .swal2-cancel:hover {
  background-color: #CBD5E1 !important;
}

.cash-call-management-page .swal2-icon-warning {
  border-color: #F6AD55 !important;
  color: #DD6B20 !important;
}

.cash-call-management-page .swal2-icon-success {
  border-color: #68D391 !important;
  color: #38A169 !important;
}

.cash-call-management-page .swal2-icon-error {
  border-color: #FEB2B2 !important;
  color: #E53E3E !important;
}

/* SPACING BETWEEN SWEETALERT BUTTONS */
.cash-call-management-page .swal2-actions {
  gap: 18px !important;    /* Add space between DELETE and CANCEL */
  margin-top: 20px !important;
}

/* LIMEIQ PRIMARY BUTTON (MATCHES NEW / DOWNLOAD BUTTON COLOR) */
.cash-call-management-page .swal2-confirm.limeiq-btn {
  background-color: #1c4a50 !important;  /* <-- LimeIQ teal/dark green */
  color: white !important;
  border-radius: 8px !important;
  padding: 10px 26px !important;
  font-weight: 600 !important;
  border: none !important;
  box-shadow: none !important;
}

.cash-call-management-page .swal2-confirm.limeiq-btn:hover {
  background-color: #0B5345 !important;  /* darker hover */
}

/* CANCEL BUTTON */
.cash-call-management-page .swal2-cancel.gray-btn {
  background-color: #E2E8F0 !important;
  color: #333 !important;
  border-radius: 8px !important;
  padding: 10px 26px !important;
  font-weight: 500 !important;
}

.cash-call-management-page .swal2-cancel.gray-btn:hover {
  background-color: #CBD5E1 !important;
}

.status-dot {
  width: 17px;
  height: 17px;
  border-radius: 50%;
  display: inline-block;
  cursor: pointer;
}

.status-dot.paid {
  background-color: #38a169; /* green */
}

.status-dot.cancelled {
  background-color: #ecc94b; /* yellow */
}

.status-dot.rejected {
  background-color: #e53e3e; /* red */
}

.status-dot.disabled {
  opacity: 0.3;
  cursor: not-allowed;
  pointer-events: none;
}

.limeiq-modal {
  text-align: left;
  padding: 10px 10px 0;
}

.limeiq-summary {
  background: #f3f9f7;
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 20px;
  text-align: center;
}

.limeiq-money {
  font-size: 15px;
  font-weight: 600;
  color: #1c7c54;
  margin-top: 6px;
}

.limeiq-breakdown {
  display: flex;
  justify-content: center;
  gap: 20px;
  font-weight: 600;
  margin-top: 6px;
  color: #1c7c54;
}

.limeiq-field {
  margin-top:15px !important;
  margin-bottom: 14px;
}

.limeiq-field label {
  display: block;
  font-weight: 600;
  margin-bottom: 6px;
  font-family: "Segoe UI Regular", sans-serif;
  color: #102F3D;
}

.limeiq-input {
  width: 60%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  font-size: 14px;
}

.limeiq-textarea {
  width: 100%;
  min-height: 90px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  font-size: 14px;
}

/* partial fee styles */ 

.limeiq-partial-section {
  margin-top: 18px;
}

#paidFeeSection {
  margin-top: 14px !important;
}

.limeiq-fee-scroll {
  max-height: 180px;       /* keeps modal stable */
  overflow-y: auto;
  padding-right: 6px;
}

/* .limeiq-fee-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));  
  gap: 0.6rem 1rem;
} */

.limeiq-fee-item {
  display: flex;
  /* flex-direction: column; */
}

.limeiq-sub-label {
  font-weight: 400;
  margin-bottom: 6px;
  color: #102F3D;
  font-family: "Segoe UI Regular", sans-serif;
  font-size: 12px;
}

.limeiq-input-full {
  width: 320px !important; /* override existing 60% */
}

.limeiq-fee-total {
  display: flex;
  justify-content: start;
  margin-top: 10px;
  font-weight: 600;
  color: green !important;
}

/* Keep Swal inside viewport */
.cashcall-paid-modal {
  max-height: 90vh !important;
    display: flex !important;
    flex-direction: column !important;
    padding-bottom: 0.8rem !important;
}

/* 2. ONLY BODY scrolls (critical fix) */
.swal2-html-container {
    overflow-y: auto !important;
    max-height: calc(90vh - 140px) !important; 
    /* 140px ≈ header + buttons safety space */
    padding-right: 6px !important;
}

/* 3. Prevent layout jump */
.swal2-actions {
    margin-top: 0.8rem !important;
    flex-shrink: 0 !important;
}

/* 4. Fee list scroll area */
.limeiq-fee-scroll {
    max-height: 220px;
    overflow-y: auto;
}

/* Optional density tuning */
.limeiq-field {
    margin-bottom: 0.5rem;
}

.limeiq-modal {
    font-size: 14px;
}

.limeiq-summary {
    padding: 0.75rem 1rem;
}

/* Cash Calls – allow top action table to expand naturally */
/* Cash Calls page – REMOVE vertical clipping from top layout */

.cash-call-management-page,
.cash-call-management-page * {
    min-height: 0 !important;
}

.cash-call-management-page .content,
.cash-call-management-page .page-content,
.cash-call-management-page .main-content,
.cash-call-management-page .layout-content {
    height: auto !important;
    max-height: none !important;
    overflow: visible !important;
}

.cash-call-management-page .toptable .b-table-sticky-header {
  max-height: 300px !important;
}

.cash-call-management-page .bottomtable .table.b-table td {
  padding-top: 1px !important;
  padding-bottom: 1px !important;
  font-size: 12px !important;
}

.cash-call-management-page .bottomtable .table.b-table th {
  padding-top: 6px !important;
  padding-bottom: 6px !important;
  font-size: 12px !important;
}

.cash-call-management-page .badge-warning {
  background-color: #FCE40082 !important;
  color: #a38631 !important
}

</style>